import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { AUTO_AB_EFFECT_TYPES } from "@/constants/game";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Dices } from "lucide-react";
import axios from "axios";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ensureInningScores, getBatterIndex, getBatterIndexKey, getLineup, showScoringToast, nextBatterIndex, rollHrDistance, isGameOver } from "./utils";
import ManagerCardHand from "./ManagerCardHand";
import HeatMapZone from "./HeatMapZone";
import { HEAT_MAP_CONFIG } from "@/constants/game";
import { resultLabelFor } from "../../utils/resultLabel";
import { nextChipSelection, isSelectionComplete } from "../../utils/chipSelection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stutter/skip diagnostics — dev only. Enable with ?diag=1 in the URL.
// Module-level so it's a stable reference and never needs to appear in dep arrays.
const _diagMode = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("diag") === "1";
function _diagLog(tag, msg, data) {
  if (!_diagMode) return;
  const ts = performance.now().toFixed(1);
  if (data !== undefined) console.log(`[${ts}ms] [PP:${tag}] ${msg}`, data);
  else console.log(`[${ts}ms] [PP:${tag}] ${msg}`);
}

function ModInput({ value, onChange, label }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[10px] text-slate-500 font-bold">{label}</span>
      <input
        type="text" inputMode="numeric" value={value}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "" || v === "-" || /^-?\d$/.test(v)) onChange(v);
        }}
        placeholder="0"
        className="w-8 h-7 text-xs text-center font-mono font-bold bg-slate-800/50 border border-slate-700/50 rounded-md text-slate-300 outline-none focus:ring-1 focus:ring-slate-500/30 transition-all"
      />
    </div>
  );
}

const PITCH_TYPES = [
  { abbr: "FB", name: "Fastball" },
  { abbr: "CH", name: "Changeup" },
  { abbr: "CB", name: "Curveball" },
  { abbr: "SL", name: "Slider" },
];

// Chip keyboard shortcuts. Digits map to chip number; letters to the pitch-type
// initial — B is curveBall because C is already Changeup. Keyed off PITCH_TYPES
// order (1=FB 2=CH 3=CB 4=SL), never off DOM order: the chip row is
// drag-reorderable and that order is persisted per session.
const KEY_TO_CHIP = { "1": 1, "2": 2, "3": 3, "4": 4, f: 1, c: 2, b: 3, s: 4 };


// Mirror of server-side _count_prime_player_categories — counts career-peak categories for dynamic card bonuses.
function countPeakCategories(player, cats, lowerBetter = new Set()) {
  const seasons = player?.seasons || [];
  if (seasons.length < 2) return 0;
  const last = seasons[seasons.length - 1]?.stats || {};
  const prior = seasons.slice(0, -1);
  let count = 0;
  for (const cat of cats) {
    const lastVal = last[cat] ?? 0;
    if (lowerBetter.has(cat)) {
      const valid = prior.map(s => s?.stats?.[cat] ?? 0).filter(v => v > 0);
      if (lastVal > 0 && valid.length > 0 && lastVal <= Math.min(...valid)) count++;
    } else {
      const best = Math.max(...prior.map(s => s?.stats?.[cat] ?? 0));
      if (lastVal >= best) count++;
    }
  }
  return count;
}

// Mirror of the server-side result mapping in PitchingPhase.rollBothDice — keeps the visual labels consistent

function SortableChip({ n, pt, selected, accent, onClick, depleted = false, bluffValue = null, inventoryCount = null, chipValueLabel = null, slot = null, bluffValueOwner = null }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: n });
  // dnd-kit stamps role="button" tabIndex={0} onto the drag wrapper. Since only a
  // PointerSensor is registered, that wrapper is a second, unlabeled, keyboard-dead
  // "button" sitting in front of the real one — so every role=button lookup returns
  // two nodes per chip and the outer one does nothing when activated. Keep the drag
  // wiring, drop the phantom button semantics.
  const { role: _dndRole, tabIndex: _dndTabIndex, ...dragAttrs } = attributes;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
    touchAction: "none",
  };
  return (
    <div ref={setNodeRef} style={style} {...dragAttrs} {...listeners} className="relative">
      <button
        type="button"
        onClick={depleted ? undefined : onClick}
        title={depleted ? `${pt.name} (depleted)` : pt.name}
        disabled={depleted}
        data-testid={`chip-${pt.abbr.toLowerCase()}`}
        // Full-name alias. The agent launcher advertised
        // chip-fastball/chip-changeup/chip-curveball/chip-slider, but the testid
        // is the abbreviation — so those selectors matched NOTHING and a driver
        // calling .click() on them saw a silent no-op. It was filed as a
        // pointer-binding defect; it was a selector mismatch. The docs are fixed,
        // and this alias means either form resolves.
        data-chip-name={pt.name.toLowerCase()}
        data-chip={n}
        data-chip-abbr={pt.abbr}
        data-selected={String(!!selected)}
        data-depleted={String(depleted)}
        {...(bluffValue != null ? { "data-chip-value": bluffValue } : {})}
        {...(bluffValueOwner ? { "data-chip-value-owner": bluffValueOwner } : {})}
        data-depleted-reason={depleted ? "no-supply" : undefined}
        {...(slot ? { "data-slot": slot } : {})}
        aria-pressed={!!selected}
        aria-label={[
          pt.name,
          bluffValue != null ? `your value ${bluffValue}` : null,
          depleted ? "depleted" : null,
        ].filter(Boolean).join(", ")}
        className={`w-12 h-12 rounded-full flex items-center justify-center font-black text-[12px] border-2 transition-all select-none ${depleted ? "border-slate-800 bg-slate-900/60 text-slate-700 cursor-not-allowed line-through" : selected ? `${accent.border} ${accent.bg} ${accent.text} shadow-lg` : "border-slate-700 bg-slate-800/60 text-slate-400 hover:border-slate-500 hover:bg-slate-800"}`}
      >
        {pt.abbr}
      </button>
      {bluffValue != null && (
        // This is always YOUR OWN pitcher's Got Your Number value, never the
        // opponent's — but it used to render as a bare unattributed digit, and two
        // separate playtest reports concluded from it that the game was leaking the
        // opposing pitcher's secret sheet. (Their cross-check was confounded by the
        // pot rule, max(your value, theirs): guessing your own 3 shows a 3-point pot
        // whatever the pitcher holds.) The number is genuinely decision-relevant, so
        // label it rather than remove it.
        <span
          title={bluffValueOwner
            ? `${bluffValueOwner}: ${pt.name} = ${bluffValue} (your sheet — the pot is the higher of the two)`
            : `Your ${pt.name} value: ${bluffValue}`}
          aria-label={bluffValueOwner
            ? `Your pitcher ${bluffValueOwner}, ${pt.name} value ${bluffValue}`
            : `Your ${pt.name} value ${bluffValue}`}
          data-testid={`chip-value-${pt.abbr.toLowerCase()}`}
          data-own-value={bluffValue}
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-amber-500 text-[10px] font-black text-black flex items-center justify-center shadow-sm cursor-help"
        >
          {bluffValue}
        </span>
      )}
      {chipValueLabel != null && (
        <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[8px] font-black text-emerald-400 bg-slate-900 rounded px-0.5 pointer-events-none leading-tight">
          {chipValueLabel}
        </span>
      )}
      {inventoryCount != null && inventoryCount !== Infinity && (
        <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[8px] font-black text-slate-400 bg-slate-900 border border-slate-700 rounded px-0.5 pointer-events-none leading-tight">
          ×{inventoryCount}
        </span>
      )}
    </div>
  );
}

// Builds the ordered list of modifiers for one side of a pitch resolution.
// Used by the mod-reveal animation and the running-total die value.
function buildPitchModList(r, side) {
  if (!r) return [];
  const mods = [];
  if (r.heatmap_positional_overlap != null) {
    const ob = r.heat_map_overlap_bonus ?? 1;
    const cb = r.heat_map_chip_bonus ?? 1;
    if (ob > 0) { const w = r.heatmap_positional_overlap ? "batter" : "pitcher"; if (w === side) mods.push({ name: "Zone",  value: ob }); }
    if (cb > 0) { const w = r.heatmap_type_match          ? "batter" : "pitcher"; if (w === side) mods.push({ name: "Type",  value: cb }); }
  } else if (r.chip_bonus > 0 && r.chip_winner === side) {
    mods.push({ name: "Chip", value: r.chip_bonus });
  }
  const breakdown = side === "pitcher" ? r.pitcher_mod_breakdown : r.batter_mod_breakdown;
  if (breakdown?.length > 0) {
    breakdown.forEach(m => mods.push({ name: m.name, value: m.value }));
  } else {
    const cm = side === "pitcher" ? (r.pitcher_card_mod || 0) : (r.batter_card_mod || 0);
    if (cm !== 0) mods.push({ name: "Cards", value: cm });
  }
  const carry = side === "pitcher" ? r.carry_pitcher_mod : r.carry_batter_mod;
  if (carry != null && carry !== 0) mods.push({ name: "Carry", value: carry });
  return mods;
}

// ── Stage-7 modifier-reveal choreography ─────────────────────────────────────
// Modifiers reveal ONE AT A TIME across both columns — the pitcher's stack
// first, then the batter's. As each reveals, its die TICKS from its current
// value to the new total, one integer per MOD_TICK_MS (up for +mods, down for
// −mods), then holds MOD_GAP_MS before the next modifier.
const MOD_TICK_MS = 100;    // per-integer die step
const MOD_GAP_MS = 220;     // hold between one modifier finishing and the next
const MOD_LEAD_MS = 200;    // head start before the first modifier
const MOD_SETTLE_MS = 400;  // hold after the last modifier before the result badge

// Flat, ordered list of every modifier on the pitch, each tagged with its side.
// Ball in Play has no pitcher roll, so the pitcher stack is dropped.
function pitchModSequence(r) {
  if (!r) return [];
  const isBip = (r.triggered_effects || []).includes("ball_in_play");
  const pMods = isBip ? [] : buildPitchModList(r, "pitcher");
  const bMods = buildPitchModList(r, "batter");
  return [
    ...pMods.map(m => ({ ...m, side: "pitcher" })),
    ...bMods.map(m => ({ ...m, side: "batter" })),
  ];
}

// Total wall-clock time the stage-7 reveal needs — keeps the stage hold and the
// reveal effect in lockstep so the result badge never cuts a tick off.
function pitchModRevealMs(r) {
  const seq = pitchModSequence(r);
  if (seq.length === 0) return 350;
  const ticks = seq.reduce((s, m) => s + Math.abs(m.value), 0);
  return MOD_LEAD_MS + ticks * MOD_TICK_MS + seq.length * MOD_GAP_MS + MOD_SETTLE_MS;
}

export default function PitchingPhase({
  phaseSync = null,
  onPhaseSyncApplied = null,
  game, gameId, onUpdate: rawOnUpdate, setCurrentPhase, setPitchResult,
  onChipsUsed, onRecordPlay, onLogPlay,
  // PvP props
  isPvP = false, homeHand = [], awayHand = [], allCards = [],
  homeSpentCards = [], awaySpentCards = [],
  onCardsPlayed, onHalfInningEnd, onInstantCardPlayed = null, onHackTheBoardPlay = null,
  // Online props
  isOnline = false, gameStarted = true, myRole = null, wsSend = null, wsConnected = true,
  onlinePitcherLocked = null, onlinePitcherReady = null, onlinePitchReveal = null, onClearPitchReveal = null,
  onResultRevealed = null,
  onlineStealActive = null, onlineStealOpponentLocked = null, onlineStealResolution = null,
  onClearStealResolution = null,
  onlinePickoffSelect = null,
  onlinePickoffRoll = null, onClearPickoffRoll = null,
  onPitchClockUpdate = null, pitchClockSync = null,
  handRefreshActive = false,
  gameOver = false,
  // Fired the instant the resolution-stage badge appears for an auto-HR (contact ≥7).
  // GameBoard hooks this to setHrTriggerKey so the HR ball cinematic launches in
  // sync with the on-screen "CRUSHED!" / "Smashed!" badge, not before or after.
  onHrShow = null,
  // Phase 5/6: each side's persistent active cards (from server). Passed through
  // to ManagerCardHand so cards in play render with ACTIVE badge + ✕ button.
  homeActiveCards = [], awayActiveCards = [],
  onDeactivateCard = null,
  activeListeners = {},
  onToggleListener = null,
  onSpendLocalCards = null,
  // Chip Arsenal pitch mode
  chipArsenal = false,
  chipArsenalInfinite = 1,
  chipArsenalModifiers = [1, 1, 1, 1],
  // Heat Map pitch mode
  heatMap = false,
  pitcherHeatHistory = [],
  // Controlled state lifted to GameBoard so HeatMapZone renders in the center field area.
  heatMapChip = null,
  onHeatMapChipChange = null,
  heatMapPos = null,
  onHeatMapPosChange = null,
  heatMapDropSeq = 0,
  heatMapOverhangThreshold = 20,
  heatMapZoneWidthPct = 100,
  onPvpPhaseChange = null,
  bluffModalOpen = false,
  keyboardBlocked = false,
  opponentBluffReady = true,
  lockinReentry = null,
  onLockinReentryConsumed = null,
  rejectedCardId = null,
  onRejectedCardClear = null,
  pitchLockInRejectedSeq = 0,
}) {
  // Wrap onUpdate so every state mutation triggered by recordAndAdvance / handle* automatically
  // carries the current resolution_id, making the PUT idempotent on the server.
  const onUpdate = useCallback((newState) => {
    const rid = onlinePitchReveal?.resolution_id || null;
    rawOnUpdate(newState, rid);
  }, [rawOnUpdate, onlinePitchReveal]);

  // Standard roll state
  const [pitcherRoll, setPitcherRoll] = useState("");
  const [pitcherManager, setPitcherManager] = useState("");
  const [pitcherChips, setPitcherChips] = useState("");
  const [batterRoll, setBatterRoll] = useState("");
  const [batterManager, setBatterManager] = useState("");
  const [batterChips, setBatterChips] = useState("");
  const [result, setResult] = useState(null);
  const [isRolling, setIsRolling] = useState(false);
  const [processing, setProcessing] = useState(false);
  const rollInterval = useRef(null);

  // Online 60-second action timer (MY turn)
  const [actionTimer, setActionTimer] = useState(null);
  const timerRef = useRef(null);
  // Mirror countdown shown while waiting for opponent (display only, no auto-action)
  const [opponentTimer, setOpponentTimer] = useState(null);
  const opponentTimerRef = useRef(null);
  // Legacy 3-second pre-roll countdown (debug mode only)
  const [revealCountdown, setRevealCountdown] = useState(null);
  const revealCountdownRef = useRef(null);
  // Unified resolution animation: 0 init → 1 chips → 2 cards → 3 dice spin → 4 dice land → 5 result → 6 done
  const [resolutionStage, setResolutionStage] = useState(0);
  const [diceDisplay, setDiceDisplay] = useState({ p: 0, b: 0 });
  const [pitcherDieAnimated, setPitcherDieAnimated] = useState(null);
  const [batterDieAnimated, setBatterDieAnimated] = useState(null);
  const [modRevealIndex, setModRevealIndex] = useState(0);
  const diceSpinRef = useRef(null);
  const [pickoffDiceLanded, setPickoffDiceLanded] = useState(false);
  const [pickoffSelectTimer, setPickoffSelectTimer] = useState(null);
  const pickoffSelectTimerRef = useRef(null);
  // Old Dog auto-fire modal: shows negated card list when 2+ cards were negated
  const [oldDogModal, setOldDogModal] = useState(null);

  // Debug mode keeps the legacy manual D10 inputs reachable via ?debug=1
  const debugMode = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debug") === "1";

  const _prevPvpPhaseRef = useRef(null);
  const _prevStageRef = useRef(0);
  const _stageEnteredAtRef = useRef(0);

  // PvP phase machine — unified for online: both players use pitch_select simultaneously.
  const [pvpPhase, setPvpPhase] = useState("pitcher_select");
  // Online phases: pitch_select (pitcher) | waiting_for_pitcher (batter) | batter_select | waiting_for_opponent | reveal | rolling
  // Local PvP phases: pitcher_select | cover_to_batter | batter_select | reveal | rolling

  // Diagnostic: log pvpPhase transitions (dev + ?diag=1 only)
  useEffect(() => {
    if (!_diagMode) return;
    const prev = _prevPvpPhaseRef.current;
    _prevPvpPhaseRef.current = pvpPhase;
    if (prev !== null) _diagLog("PHASE", `${prev} → ${pvpPhase}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pvpPhase]);
  const [pitcherChipPick, setPitcherChipPick] = useState(null);
  const [batterChipGuess, setBatterChipGuess] = useState(null);
  const [batterChipGuess2, setBatterChipGuess2] = useState(null);  // Tipping Pitches: second chip guess
  const [pitcherPlayedCards, setPitcherPlayedCards] = useState([]);
  const [batterPlayedCards, setBatterPlayedCards] = useState([]);
  const [chipBonus, setChipBonus] = useState(null);
  // Heat Map pitch mode: pixel-normalized {x,y} placement positions
  const [pitcherPosition, setPitcherPosition] = useState(null);
  const [batterPosition, setBatterPosition] = useState(null);
  const [myPosition, setMyPosition] = useState(null);  // online: my own position before reveal
  // Cards I've selected to play during a steal — separate pile so it doesn't bleed into the
  // pitch flow if the at-bat resumes after the steal.
  const [stealCards, setStealCards] = useState([]);
  // Draggable chip order — 1-based indices; reordering is purely cosmetic/preference.
  // Persists to sessionStorage so order survives page refreshes within the tab
  // session but doesn't accumulate per-game keys forever (which previously caused
  // QuotaExceededError after many dev sessions).
  const [chipOrder, setChipOrder] = useState(() => {
    try {
      const key = `chipOrder-${gameId}`;
      const saved = sessionStorage.getItem(key);
      return saved ? JSON.parse(saved) : [1, 2, 3, 4];
    } catch {
      return [1, 2, 3, 4];
    }
  });
  const chipSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [chipPanelWidth, setChipPanelWidth] = useState(240);
  const [chipPanelDragging, setChipPanelDragging] = useState(false);
  const chipPanelContainerRef = useRef(null);

  const battingTeam = game.state.half === "top" ? game.away_team : game.home_team;
  const fieldingTeam = game.state.half === "top" ? game.home_team : game.away_team;
  const batters = getLineup(battingTeam);
  const batterIndex = getBatterIndex(game.state) % (batters.length || 1);
  const currentBatter = batters[batterIndex];
  const currentPitcher = fieldingTeam.players.find(p => p.position === "P" && !p.subbed_out);
  const _reliever_available = fieldingTeam.players.filter(p => p.position === "P" && !p.subbed_out).length > 1;
  const _discard_count = (game?.discard || []).length;

  // Which hands / spent piles belong to pitcher/batter in PvP
  const pitchingTeamKey = game.state.half === "top" ? "home" : "away";
  const battingTeamKey = pitchingTeamKey === "home" ? "away" : "home";
  const pitchingHand = pitchingTeamKey === "home" ? homeHand : awayHand;
  const battingHand = battingTeamKey === "home" ? homeHand : awayHand;
  const pitchingSpent = pitchingTeamKey === "home" ? homeSpentCards : awaySpentCards;
  const battingSpent = battingTeamKey === "home" ? homeSpentCards : awaySpentCards;
  // Phase 5/6: per-side active cards routed by current half (matches the hand routing).
  const pitchingActive = pitchingTeamKey === "home" ? homeActiveCards : awayActiveCards;
  const battingActive = battingTeamKey === "home" ? homeActiveCards : awayActiveCards;

  const pitcherTotal = useMemo(() => (parseInt(pitcherRoll) || 0) + (parseInt(pitcherManager) || 0) + (parseInt(pitcherChips) || 0), [pitcherRoll, pitcherManager, pitcherChips]);
  const batterTotal = useMemo(() => (parseInt(batterRoll) || 0) + (parseInt(batterManager) || 0) + (parseInt(batterChips) || 0), [batterRoll, batterManager, batterChips]);

  // Online: determine if I'm the pitcher or batter this half-inning
  const amIPitcher = isOnline && myRole === pitchingTeamKey;
  const amIBatter = isOnline && myRole === battingTeamKey;

  // ── Keyboard shortcuts ──────────────────────────────────────────────────────
  // Each phase branch below defines its own handleLockIn / handleCallBall and its
  // own chip setter, so the actions aren't reachable from component scope. The
  // active branch publishes its action set here and one listener reads it.
  // Cleared on every render so a branch that doesn't publish (resolution,
  // waiting screens) leaves no stale action wired to Enter.
  // Per-pitch submit guard. A driver acting on awaitingMyAction re-fired lock-in in
  // a loop, and a duplicate landing after the resolver popped pending_pitches created
  // a phantom pitch that deadlocked both seats. Cleared whenever a new pitch begins
  // (fresh token) or a reveal lands.
  const _submittedTokenRef = useRef(null);
  const _lockToken = pitchClockSync?.pitch_token ?? null;
  const alreadySubmitted = _lockToken != null && _submittedTokenRef.current === _lockToken;

  const kbdActionsRef = useRef(null);
  kbdActionsRef.current = null;
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Key auto-repeat would fire ~30 actions/sec from one held key. `w` in
      // particular maps to intentional_walk, and the server's handler is NOT
      // idempotent — each repeat walks the NEXT batter and can force runs in.
      if (e.repeat) return;
      // A blocking overlay owns the keyboard. The Streaker mini-game binds w/a/s/d
      // on window and the server deliberately SUSPENDS the pitch clock while it
      // runs, so PitchingPhase stays mounted in pitch_select underneath it — both
      // listeners fire and moving the streaker would issue an intentional walk.
      if (keyboardBlocked) return;
      const t = e.target;
      // Only bail for genuine text entry. The earlier `t.closest("[role='dialog']")`
      // check was too broad: focus commonly REMAINS inside a dialog subtree after it
      // closes (or on a library focus-guard node), so every shortcut silently no-oped
      // until the player clicked the field again. keyboardBlocked above already
      // covers actually-open modals, computed from real state rather than from
      // wherever focus happens to be sitting.
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable
                || t.tagName === "SELECT")) return;
      const a = kbdActionsRef.current;
      if (!a) return;
      const key = (e.key || "").toLowerCase();
      const chip = KEY_TO_CHIP[key];
      if (chip != null) {
        if (!a.selectChip) return;
        e.preventDefault();
        a.selectChip(chip);
        return;
      }
      if (key === "enter" && a.lockIn) { e.preventDefault(); a.lockIn(); return; }
      if (key === "t" && a.takePitch) { e.preventDefault(); a.takePitch(); return; }
      if (key === "w" && a.walk) { e.preventDefault(); a.walk(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [keyboardBlocked]);

  // Persist chip order to sessionStorage whenever it changes. Wrapped so any
  // QuotaExceededError or disabled-storage scenario fails silently instead of
  // bubbling up and tripping the GameErrorBoundary.
  useEffect(() => {
    try {
      const key = `chipOrder-${gameId}`;
      sessionStorage.setItem(key, JSON.stringify(chipOrder));
    } catch {
      // ignore — preference persistence is best-effort
    }
  }, [chipOrder, gameId]);

  // lockin_reentry: after Foul Play / Challenge Play the server returns cards and sends
  // a pre-selection list. Pre-check those cards on the lock-in screen, then clear.
  useEffect(() => {
    if (!lockinReentry || lockinReentry.length === 0) return;
    if (amIPitcher) {
      setPitcherPlayedCards(prev => {
        const next = [...prev];
        for (const id of lockinReentry) if (!next.includes(id)) next.push(id);
        return next;
      });
    } else if (amIBatter) {
      setBatterPlayedCards(prev => {
        const next = [...prev];
        for (const id of lockinReentry) if (!next.includes(id)) next.push(id);
        return next;
      });
    }
    onLockinReentryConsumed?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockinReentry]);

  // Bluff Values: each side shows their own pitcher's chip value assignments.
  // Only shown in online mode (each player has their own screen — no spoilers).
  const _bluffValuesOn = !!game.bluff_values;
  const _battingActivePitcher = battingTeam.players.find(p => p.position === "P" && !p.subbed_out);
  const _myActivePitcher = amIPitcher ? currentPitcher : _battingActivePitcher;
  // Bluff values per chip tile: online → own pitcher's values always; local → phase-aware
  // (pitcher sees their own values during pitch_select; batter sees theirs during batter_select).
  const _myChipValues = _bluffValuesOn ? (() => {
    if (isOnline) return _myActivePitcher?.pitch_chip_values || null;
    if (pvpPhase === "pitch_select") return currentPitcher?.pitch_chip_values || null;
    if (pvpPhase === "batter_select") return _battingActivePitcher?.pitch_chip_values || null;
    return null;
  })() : null;
  const _onDeckBatter = batters.length > 1 ? batters[(batterIndex + 1) % batters.length] : null;
  const _activeCount = (roleKey) => (game?.[`${roleKey}_active_cards`] || []).filter(a => a && !a.burned).length;

  // Two-chip-guess UI — shared by Tipping Pitches (persistent, inning-scope) and
  // Discerning Eye (at-bat-scope). Both let the batter submit a second chip and
  // the server counts a match by EITHER. The UI requires both chips before lock-in.
  const _tippingPitchesActive = battingActive.some(c => c && c.id === "tipping_pitches" && !c.burned);
  const _discerningEyeCarry = game.state?.pending_at_bat_chip_bonus;
  const _discerningEyeActive = !!(
    _discerningEyeCarry &&
    _discerningEyeCarry.card_id === "discerning_eye" &&
    _discerningEyeCarry.team === battingTeamKey
  );
  // Activation pitch: card was just selected for lock-in (carry isn't set yet).
  const _discerningEyeLockedInNow = batterPlayedCards.includes("discerning_eye");
  const _allowTwoChips = _tippingPitchesActive || _discerningEyeActive || _discerningEyeLockedInNow;

  // True during the "pending" phase (half flipped, animation still in flight, modal not yet
  // open) AND the "open" phase. Suppresses clock requests and auto-action earlier than
  // handRefreshActive (which only becomes true when phase === "open"). Without this, both
  // clients can fire pitch_lock_in during the ~700ms pending window before the modal opens.
  const handRefreshInFlight = handRefreshActive || !!game.state.hand_refresh;

  // Depleting chips: pitcher's pool shrinks when batter guesses correctly.
  // Falls back to full [1..chipCount] when the setting is off or pool is unset.
  const depletingChips = !!game.depleting_chips;
  // chip_arsenal forces all 4 types available regardless of pitcher's pitch_chips count.
  const _fullPool = chipArsenal ? [1, 2, 3, 4] : Array.from({ length: currentPitcher?.pitch_chips || 4 }, (_, i) => i + 1);
  const chipPool = depletingChips ? (game.state?.pitcher_chip_pool || _fullPool) : _fullPool;

  // pitch_inventory was removed — it was a subset of chip_arsenal with hardcoded
  // FB-is-infinite + CH+1/CB+2/SL+2 modifiers. The constants below keep the surrounding
  // dependent conditionals dead-code so we don't have to scrub every call site at once.
  const pitchInventoryOn = false;
  const _getRemaining = () => Infinity;
  const _CHIP_VALUE_LABEL = ["", "", "", "", ""];

  // Streak Yourself eligibility — online H2H or vs-bot only. The Streaker
  // card's mode picker uses this to decide whether to show the interactive
  // option. ManagerCardHand also checks for a keyboard locally so mobile is
  // covered there. Server enforces the same gate on the play handler.
  const _streakerInteractiveEligible =
    isOnline || (game?.bot_role === "home" || game?.bot_role === "away");

  // Chip Arsenal supply: per-half-inning budget per chip type (all 4 types available but limited).
  // Infinite chip is exempt. chip_arsenal_supply state: { [chipN]: remainingCount }
  const _caInfinite = game.chip_arsenal_infinite || 1;
  const _caSupplyDefaults = {
    1: game.chip_arsenal_fb_count || 8,
    2: game.chip_arsenal_ch_count || 5,
    3: game.chip_arsenal_cb_count || 3,
    4: game.chip_arsenal_sl_count || 2,
  };
  const _caSupplyState = game.state?.chip_arsenal_supply || null;
  const _getCaRemaining = (n) => {
    if (!chipArsenal) return Infinity;
    if (n === _caInfinite) return Infinity;
    if (_caSupplyState) return _caSupplyState[n] ?? _caSupplyDefaults[n];
    return _caSupplyDefaults[n];
  };

  // Pitch Arsenal: batters get unlimited chips and must not see the pitcher's
  // remaining inventory. Hide depletion from any viewer acting as the batter
  // (online H2H batter, or human-as-batter in a bot game). Note the filter must
  // apply to BOTH inventory modes — the chipArsenal branch previously omitted
  // `!_viewerIsBatter`, leaking depleted-supply state through the strikethrough
  // tile visual.
  const _viewerIsBatter = amIBatter || (!!game?.bot_role && game.bot_role !== "both" && game.bot_role === pitchingTeamKey);
  const isChipDepleted = (n) => {
    if (depletingChips && !chipPool.includes(n)) return true;
    if (pitchInventoryOn && !_viewerIsBatter && n !== 1 && _getRemaining(n) <= 0) return true;
    if (chipArsenal && !_viewerIsBatter && n !== _caInfinite && _getCaRemaining(n) <= 0) return true;
    return false;
  };

  // Online: both players start in pitch_select simultaneously (unified flow).
  // Don't blow away an in-flight steal/pitch resolution — the half can flip mid-resolution
  // (e.g., caught stealing for the 3rd out) and we want the players to see the outcome
  // before kicking back to pitch select.
  useEffect(() => {
    if (!isOnline || !myRole) return;
    if (pvpPhase === "steal_select" || pvpPhase === "steal_waiting" || pvpPhase === "steal_resolution") return;
    if (pvpPhase === "pickoff_select" || pvpPhase === "pickoff_roll") return;
    if (pvpPhase === "resolution") return;
    if (pvpPhase === "waiting_for_pitcher" || pvpPhase === "batter_select" || pvpPhase === "waiting_for_opponent") return;
    const isBotGame = !!game?.bot_role;
    if (!isBotGame && amIBatter) {
      setPvpPhase("waiting_for_pitcher");
    } else {
      setPvpPhase("pitch_select");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, myRole, game.state.half, game.state.inning, game?.bot_role]);

  // Online (PvP): when pitcher_ready arrives, transition batter from waiting_for_pitcher to batter_select.
  useEffect(() => {
    if (!isOnline || !onlinePitcherReady || !amIBatter) return;
    if (pvpPhase === "batter_select" || pvpPhase === "waiting_for_opponent") return;
    setPvpPhase("batter_select");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, onlinePitcherReady, amIBatter]);

  // Redundant advance path for the batter, derived from a BROADCAST.
  //
  // `pitcher_ready` is unicast, so it is a single point of failure: miss it and the
  // batter is stranded in waiting_for_pitcher forever while the pitcher waits on
  // them. But the same commit also broadcasts pitch_clock_start{kind:"pitch_batter"}
  // to BOTH seats — a signal that means "the pitcher has committed" and that this
  // client was already receiving and ignoring.
  //
  // Consuming it gives two independent paths to the same transition, so no single
  // dropped frame can strand a seat. Idempotent: the guards below make a duplicate
  // advance a no-op, so both paths firing is harmless.
  useEffect(() => {
    if (!isOnline || !amIBatter) return;
    if (pvpPhase !== "waiting_for_pitcher") return;
    if (pitchClockSync?.kind !== "pitch_batter") return;
    setPvpPhase("batter_select");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, amIBatter, pvpPhase, pitchClockSync?.kind]);

  // Fix 2: Recover from stuck waiting_for_opponent when server rejects our lock-in.
  // GameBoard increments pitchLockInRejectedSeq on action_rejected for pitch_lock_in,
  // or when phase_snapshot shows we're not in pending_pitches but opponent is.
  useEffect(() => {
    if (!pitchLockInRejectedSeq || pvpPhase !== "waiting_for_opponent") return;
    setPvpPhase(amIBatter ? "batter_select" : "pitch_select");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pitchLockInRejectedSeq]);

  // Stuck in waiting_for_opponent? ASK the server — never assume.
  //
  // This used to blindly `setPvpPhase(amIBatter ? "batter_select" : "pitch_select")`
  // after 28s, on the assumption (see the old comment) that the server's ~32s
  // auto-complete was about to fire anyway. That assumption is false under
  // no_timers, where the server does not act for 600s — so the client was
  // discarding a perfectly good committed lock 20x too early, clearing the chip
  // selection and re-enabling Lock In.
  //
  // That is not a cosmetic flicker: an enabled Lock In with
  // awaitingMyAction:true is a complete, correct invitation to submit, so any
  // well-behaved client re-sends — and two locks for one pitch is what wedged the
  // resolver. A playtester measured it as a ~15% wall-clock tax on EVERY pitch,
  // and hit it on four consecutive pitches against a slow opponent because the
  // timer re-arms each time.
  //
  // So: request state and let the phase_sync handler above decide. If the server
  // still holds our lock it keeps us in waiting_for_opponent; if the lock really
  // was dropped, phase_sync says so and we recover. The server is the authority on
  // whether we are locked in — the client never was.
  useEffect(() => {
    if (!isOnline || pvpPhase !== "waiting_for_opponent" || gameOver) return;
    const id = setInterval(() => {
      wsSend?.({ type: "request_state" });
    }, 12_000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pvpPhase, isOnline, gameOver]);

  // Symmetric watchdog for the BATTER. The one above only covers
  // waiting_for_opponent; a batter stranded by a lost unicast `pitcher_ready` sits in
  // waiting_for_pitcher, which had no watchdog at all — so the stall was silent and
  // unbounded (with no_timers the only backstop is the 600s AFK net).
  //
  // Deliberately does NOT change phase on its own: the pitcher may genuinely still be
  // deciding, and guessing would be its own desync. It just asks the server, and the
  // phase_sync handler above advances us only if the server says the pitcher really
  // has committed. Repeats, because the frame it is compensating for is one that can
  // be lost more than once.
  useEffect(() => {
    if (!isOnline || pvpPhase !== "waiting_for_pitcher" || gameOver) return;
    if (!amIBatter) return;
    const id = setInterval(() => {
      wsSend?.({ type: "request_state" });
    }, 12_000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pvpPhase, isOnline, gameOver, amIBatter]);

  // Online: when steal_declared arrives, switch to steal_select. Resets stealCards so a fresh
  // hand selection starts clean.
  useEffect(() => {
    if (!isOnline || !onlineStealActive) return;
    if (pvpPhase === "steal_select" || pvpPhase === "steal_waiting" || pvpPhase === "steal_resolution") return;
    setPvpPhase("steal_select");
    setStealCards([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, onlineStealActive]);

  // Online: when steal_resolution arrives, transition to the reveal screen and kick off
  // the staged animation (cards → dice spin → land → SAFE/OUT). Reusing `resolutionStage`
  // from the pitch flow — STAGE_DELAYS below picks the right map by phase.
  useEffect(() => {
    if (!isOnline || !onlineStealResolution) return;
    if (pvpPhase === "steal_resolution") return;
    setPvpPhase("steal_resolution");
    setResolutionStage(1);
    setPitcherDieAnimated(null);
    setBatterDieAnimated(null);
    setModRevealIndex(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, onlineStealResolution]);

  // After the steal_resolution staged animation finishes (resolutionStage hits 5), dismiss
  // to a fresh pitch_select. Always run the cleanup — including when the steal ended the
  // game — so onClearStealResolution clears the prop and GameBoard's isAnimating gate
  // releases, letting the victory modal fire after the reveal.
  useEffect(() => {
    if (pvpPhase !== "steal_resolution") return;
    if (resolutionStage < 5) return;
    onClearStealResolution?.();
    setPitcherChipPick(null);
    setBatterChipGuess(null);
    setPitcherPlayedCards([]);
    setBatterPlayedCards([]);
    setStealCards([]);
    if (!gameOver) setPvpPhase("pitch_select");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pvpPhase, resolutionStage, gameOver]);

  // Rejected card: GameBoard signals this component to clear a card from myCards
  // when the server rejects a card play. Remove it from the appropriate played-cards list.
  useEffect(() => {
    if (!rejectedCardId) return;
    const setter = amIPitcher ? setPitcherPlayedCards : setBatterPlayedCards;
    setter(prev => prev.filter(c => c !== rejectedCardId));
    onRejectedCardClear?.();
  }, [rejectedCardId, amIPitcher, onRejectedCardClear]);

  // ── Pick-off phases ────────────────────────────────────────────────────────
  // Flow: pickoff_select (fielder clicks runner tile, 15s) → pickoff_roll (dice
  // animation, 2s) → pitch_select. Single-runner path skips pickoff_select entirely.
  useEffect(() => {
    if (!isOnline || !onlinePickoffRoll) return;
    if (pvpPhase === "pickoff_roll") return;
    setPvpPhase("pickoff_roll");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, onlinePickoffRoll]);

  // Auto-dismiss the dice animation after ~2 s, then return to pitch_select.
  // Runner selection now happens BEFORE the roll (pickoff_select phase), so there
  // is no pickoff_target phase anymore.
  useEffect(() => {
    if (pvpPhase !== "pickoff_roll") return;
    const timer = setTimeout(() => {
      onClearPickoffRoll?.();
      if (!gameOver) setPvpPhase("pitch_select");
    }, 2000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pvpPhase]);

  // ── pickoff_select phase: enter when server requests target selection ─────────
  useEffect(() => {
    if (!isOnline || !onlinePickoffSelect) return;
    if (pvpPhase === "pickoff_select" || pvpPhase === "pickoff_roll") return;
    setPvpPhase("pickoff_select");
    setPickoffSelectTimer(onlinePickoffSelect.timer_secs || 15);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, onlinePickoffSelect]);

  // Client-side display countdown while in pickoff_select.
  // This is display-only — the server has an authoritative auto-select timer.
  useEffect(() => {
    if (pvpPhase !== "pickoff_select") {
      clearInterval(pickoffSelectTimerRef.current);
      setPickoffSelectTimer(null);
      return;
    }
    pickoffSelectTimerRef.current = setInterval(() => {
      setPickoffSelectTimer(prev => (prev === null || prev <= 0 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(pickoffSelectTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pvpPhase]);

  // Online: when pitch_reveal WS arrives, populate the resolution data and pick the animation path.
  // Guard only against re-entering an already-running resolution; otherwise always transition.
  useEffect(() => {
    if (!isOnline || !onlinePitchReveal) return;
    if (pvpPhase === "resolution" || pvpPhase === "reveal" || pvpPhase === "rolling") return;

    const r = onlinePitchReveal;

    // Restore chips and cards
    setPitcherChipPick(r.pitcher_chip);
    setBatterChipGuess(r.batter_chip);
    setBatterChipGuess2(r.batter_chip2 || null);
    setChipBonus({ winner: r.chip_winner, value: r.chip_bonus });
    setPitcherPlayedCards(r.pitcher_cards || []);
    setBatterPlayedCards(r.batter_cards || []);

    // Pre-fill dice + breakdown so recordAndAdvance can pick up server-authoritative values
    setPitcherRoll(String(r.pitcher_d10));
    setBatterRoll(String(r.batter_d10));
    const pChipMod = (r.pitcher_card_mod || 0) + (r.chip_winner === "pitcher" ? (r.chip_bonus || 0) : 0);
    const bChipMod = (r.batter_card_mod || 0) + (r.chip_winner === "batter" ? (r.chip_bonus || 0) : 0);
    setPitcherChips(pChipMod ? String(pChipMod) : "");
    setBatterChips(bChipMod ? String(bChipMod) : "");

    // Build the result object the way rollBothDice would, for recordAndAdvance to consume
    // Pre-pitch strike count, from the server's own snapshot. Reading
    // game.state here raced the game_state broadcast: if it had already
    // applied, strikes was POST-pitch, so a 1-2 count showed STRIKEOUT on
    // strike two, and a real strike three (count reset to 0) showed none.
    const preK = r.context?.strikes ?? game?.state?.strikes ?? 0;
    const labeled = resultLabelFor(r.result_type, r.contact || 0, preK);
    setResult({
      ...labeled,
      type: r.result_type,
      contact: r.result_type === "in_play" ? r.contact : undefined,
      pitcherTotal: r.pitcher_total,
      batterTotal: r.batter_total,
      pRoll: r.pitcher_d10,
      bRoll: r.batter_d10,
      pMgr: 0,
      pChip: pChipMod,
      bMgr: 0,
      bChip: bChipMod,
    });

    // Cards played callback (modifier accounting, etc.)
    if (onCardsPlayed) {
      const cardById = (id) => [...pitchingHand, ...battingHand].find(c => c.id === id) || allCards.find(c => c.id === id);
      const played = [
        ...(r.pitcher_cards || []).map(id => ({ ...cardById(id), playedBy: "pitcher" })),
        ...(r.batter_cards || []).map(id => ({ ...cardById(id), playedBy: "batter" })),
      ].filter(c => c && c.id);
      onCardsPlayed(played, pitchingTeamKey, battingTeamKey);
    }

    if (debugMode) {
      // Legacy path: pre-fill and let user manually click "Roll D10s" to verify totals
      setPvpPhase("reveal");
      setRevealCountdown(3);
    } else {
      // New unified resolution: animated single screen
      _diagLog("PHASE", `pitch_reveal received → resolution (rid=${r.resolution_id?.slice(-8)}, result=${r.result_type})`);
      _stageEnteredAtRef.current = performance.now();
      setPvpPhase("resolution");
      setResolutionStage(1);
      setDiceDisplay({ p: 0, b: 0 });
      setPitcherDieAnimated(null);
      setBatterDieAnimated(null);
      setModRevealIndex(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, onlinePitchReveal, pvpPhase]);

  // Legacy reveal countdown ticker (debug mode)
  useEffect(() => {
    if (revealCountdown === null) return;
    if (revealCountdown <= 0) {
      clearTimeout(revealCountdownRef.current);
      handleStartRolling();
      setRevealCountdown(null);
      return;
    }
    revealCountdownRef.current = setTimeout(() => {
      setRevealCountdown(prev => (prev === null ? null : prev - 1));
    }, 1000);
    return () => clearTimeout(revealCountdownRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealCountdown]);

  // Unified resolution animation: stage progression + dice spin. Pitch and steal phases
  // share `resolutionStage` but have different stage maps — pitch reveals chips first
  // (no chips on a steal). Both end with a result-hold beat before advancing.
  useEffect(() => {
    if (pvpPhase !== "resolution" && pvpPhase !== "steal_resolution") {
      clearInterval(diceSpinRef.current);
      return;
    }
    // Pitch reveal is now SEQUENTIAL: pitcher rolls first, then the batter responds.
    // Stages:
    //   1=chips reveal (600)
    //   2=cards reveal (700)
    //   3=pitcher dice spin (700)
    //   4=pitcher dice land (300) — pitcher's d10 frozen, batter still pending
    //   5=batter dice spin (700)
    //   6=batter dice land (300) — both d10s frozen
    //   7=result hold (3000) — totals + outcome badge appear
    //   8=advance
    // Steal stays simultaneous (single combined roll).
    const isBip = (onlinePitchReveal?.triggered_effects || []).includes("ball_in_play");
    // Ball in Play skips pitcher dice stages — jump through 3 and 4 instantly.
    // Stage 7 = mod-reveal: each modifier reveals one at a time across both
    // columns and ticks its die raw→total. pitchModRevealMs covers the whole
    // choreographed sequence so the result badge waits for the last tick.
    const _modRevealMs = pitchModRevealMs(onlinePitchReveal);
    const PITCH_DELAYS = { 1: 600, 2: 700, 3: isBip ? 1 : 700, 4: isBip ? 1 : 600, 5: 700, 6: 600, 7: _modRevealMs, 8: 3000 };
    const STEAL_DELAYS = { 1: 500, 2: 1300, 3: 1000, 4: 2000 };
    const delays = pvpPhase === "steal_resolution" ? STEAL_DELAYS : PITCH_DELAYS;
    if (resolutionStage in delays) {
      const elapsed = _stageEnteredAtRef.current ? (performance.now() - _stageEnteredAtRef.current).toFixed(1) : "?";
      _diagLog("ANIM", `stage ${resolutionStage} → ${resolutionStage + 1} (scheduled in ${delays[resolutionStage]}ms, prev stage held ${elapsed}ms)`);
      _stageEnteredAtRef.current = performance.now();
      const t = setTimeout(() => {
        _diagLog("ANIM", `stage ${resolutionStage + 1} firing`);
        setResolutionStage(s => s + 1);
      }, delays[resolutionStage]);
      return () => clearTimeout(t);
    }
    if (!(resolutionStage in delays) && resolutionStage > 0) {
      _diagLog("ANIM", `stage ${resolutionStage} — terminal (no next delay, advance via recordAndAdvance)`);
    }
  }, [pvpPhase, resolutionStage, onlinePitchReveal, onlineStealResolution]);

  // Dice spin animation. Pitch is now sequential: stage 3 spins pitcher only, stage 4
  // lands pitcher; stage 5 spins batter only, stage 6 lands batter (pitcher stays
  // frozen on its landed value). Steal stays simultaneous — both dice spin together
  // at stage 2 and land at stage 3.
  useEffect(() => {
    if (pvpPhase !== "resolution" && pvpPhase !== "steal_resolution") return;

    if (pvpPhase === "steal_resolution") {
      if (resolutionStage === 2) {
        diceSpinRef.current = setInterval(() => {
          setDiceDisplay({ p: Math.floor(Math.random() * 10), b: Math.floor(Math.random() * 10) });
        }, 60);
        return () => clearInterval(diceSpinRef.current);
      }
      if (resolutionStage >= 3 && onlineStealResolution) {
        clearInterval(diceSpinRef.current);
        setDiceDisplay({ p: onlineStealResolution.runner_d6, b: onlineStealResolution.defender_d6 });
      }
      return;
    }

    // Pitch path — sequential reveal.
    const pD20 = onlinePitchReveal?.pitcher_dice === "d20";
    const bD20 = onlinePitchReveal?.batter_dice === "d20";
    if (resolutionStage === 3) {
      // Pitcher's dice spinning. Batter held at placeholder.
      diceSpinRef.current = setInterval(() => {
        setDiceDisplay(prev => ({ ...prev, p: pD20 ? Math.floor(Math.random() * 20) + 1 : Math.floor(Math.random() * 10) }));
      }, 60);
      return () => clearInterval(diceSpinRef.current);
    }
    if (resolutionStage === 4 && onlinePitchReveal) {
      // Pitcher lands.
      clearInterval(diceSpinRef.current);
      setDiceDisplay(prev => ({ ...prev, p: onlinePitchReveal.pitcher_d10 }));
      return;
    }
    if (resolutionStage === 5) {
      // Batter starts spinning. Pitcher already landed; preserve via prev.
      const isBipSpin = (onlinePitchReveal?.triggered_effects || []).includes("ball_in_play");
      diceSpinRef.current = setInterval(() => {
        setDiceDisplay(prev => ({
          ...prev,
          b: bD20 ? Math.floor(Math.random() * 20) + 1
            : isBipSpin ? Math.floor(Math.random() * 6) + 1
            : Math.floor(Math.random() * 10),
        }));
      }, 60);
      return () => clearInterval(diceSpinRef.current);
    }
    if (resolutionStage >= 6 && onlinePitchReveal) {
      clearInterval(diceSpinRef.current);
      setDiceDisplay(prev => ({ ...prev, b: onlinePitchReveal.batter_d10 }));
    }
  }, [pvpPhase, resolutionStage, onlinePitchReveal, onlineStealResolution]);

  // Steal modifier tick: after both steal dice land (stage 3), tick each die from raw to total.
  useEffect(() => {
    if (pvpPhase !== "steal_resolution" || resolutionStage !== 3) return;
    const sr = onlineStealResolution;
    if (!sr) return;
    function startTicker(raw, total, setter) {
      if (raw === total) return () => {};
      let cur = raw;
      const step = total > raw ? 1 : -1;
      let tickId;
      const startId = setTimeout(() => {
        tickId = setInterval(() => { cur += step; setter(cur); if (cur === total) clearInterval(tickId); }, 130);
      }, 250);
      return () => { clearTimeout(startId); if (tickId) clearInterval(tickId); };
    }
    const cleanupR = startTicker(sr.runner_d6, sr.runner_total, setPitcherDieAnimated);
    const cleanupD = startTicker(sr.defender_d6, sr.defender_total, setBatterDieAnimated);
    return () => { cleanupR(); cleanupD(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pvpPhase, resolutionStage, onlineStealResolution]);

  // Pitch mod reveal: at stage 7 (after both raw dice are shown), reveal each
  // modifier ONE AT A TIME across both columns — the pitcher's stack first,
  // then the batter's. As each reveals, its die TICKS from its current value to
  // the new total, one integer per MOD_TICK_MS (up for +mods, down for −mods).
  // `modRevealIndex` drives the labels; pitcherDieAnimated/batterDieAnimated
  // hold the ticking die values (read by the render during stage 7+).
  useEffect(() => {
    if (pvpPhase !== "resolution" || resolutionStage !== 7 || !onlinePitchReveal) return;
    const r = onlinePitchReveal;
    const seq = pitchModSequence(r);
    setModRevealIndex(0);
    setPitcherDieAnimated(r.pitcher_d10 || 0);
    setBatterDieAnimated(r.batter_d10 || 0);
    if (seq.length === 0) return;
    const timeouts = [];
    let at = MOD_LEAD_MS;            // running wall-clock offset
    let pCur = r.pitcher_d10 || 0;   // pitcher die value as mods accumulate
    let bCur = r.batter_d10 || 0;    // batter die value as mods accumulate
    seq.forEach((mod, i) => {
      const revealAt = at;
      // Label fades in as the modifier's tick begins.
      timeouts.push(setTimeout(() => setModRevealIndex(i + 1), revealAt));
      const steps = Math.abs(mod.value);
      const dir = mod.value >= 0 ? 1 : -1;
      const setDie = mod.side === "pitcher" ? setPitcherDieAnimated : setBatterDieAnimated;
      const base = mod.side === "pitcher" ? pCur : bCur;
      for (let s = 1; s <= steps; s++) {
        const val = base + dir * s;
        timeouts.push(setTimeout(() => setDie(val), revealAt + s * MOD_TICK_MS));
      }
      if (mod.side === "pitcher") pCur += mod.value; else bCur += mod.value;
      at = revealAt + steps * MOD_TICK_MS + MOD_GAP_MS;
    });
    return () => timeouts.forEach(clearTimeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pvpPhase, resolutionStage, onlinePitchReveal]);

  // Pickoff dice spin — spins diceDisplay.p for 600ms then locks to the real roll value.
  useEffect(() => {
    if (pvpPhase !== "pickoff_roll") { setPickoffDiceLanded(false); return; }
    setPickoffDiceLanded(false);
    const spinId = setInterval(() => {
      setDiceDisplay(prev => ({ ...prev, p: Math.floor(Math.random() * 10) }));
    }, 60);
    const landId = setTimeout(() => {
      clearInterval(spinId);
      setPickoffDiceLanded(true);
    }, 600);
    return () => { clearInterval(spinId); clearTimeout(landId); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pvpPhase]);

  // Resolution complete → both clients call recordAndAdvance. PUTs are idempotent server-side
  // (keyed by resolution_id), so concurrent PUTs are safe and connection failure on one side is
  // covered by the other. Local stats/log updates run once per client (intended).
  useEffect(() => {
    if (pvpPhase !== "resolution" || resolutionStage < 9) return;
    // Always clear onlinePitchReveal even when the game ended — GameBoard gates its victory
    // modal on `isAnimating` (= a resolution prop is set), so we have to release that flag
    // for the modal to appear after the user has seen the play.
    if (onClearPitchReveal) onClearPitchReveal();
    recordAndAdvance();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pvpPhase, resolutionStage, gameOver]);

  // HR ball trigger — fires when the result-badge stage (7) lands for an auto-HR
  // (pitch with contact ≥ 7, server short-circuits fielding). Stage 7 is now the
  // result-hold beat after both dice have landed sequentially. Fielding-path HRs
  // are handled in FieldingPhase since that's where their result badge lives.
  const hrShownForRevealRef = useRef(null);
  useEffect(() => {
    if (pvpPhase !== "resolution") return;
    if (resolutionStage !== 8) return;
    if (!onlinePitchReveal) return;
    if (onlinePitchReveal.result_type !== "in_play") return;
    if ((onlinePitchReveal.contact || 0) < 7) return;
    // Dedup per resolution_id so a re-render doesn't double-fire.
    if (hrShownForRevealRef.current === onlinePitchReveal.resolution_id) return;
    hrShownForRevealRef.current = onlinePitchReveal.resolution_id;
    // Power = contact value for auto-HRs. Higher contact → bigger reported distance.
    // Seed = resolution_id so the cinematic and the badge agree on the distance.
    if (onHrShow) onHrShow(onlinePitchReveal.contact, onlinePitchReveal.resolution_id);
  }, [resolutionStage, pvpPhase, onlinePitchReveal, onHrShow]);

  // Play-log sync — fire onResultRevealed the moment the result badge appears so
  // GameBoard can drain the matching held log entry. Pitch reveal: stage 7
  // (result-hold beat). Steal resolution: stage 4 (result-hold beat). Without
  // this, the log entry waited until the resolution prop cleared at stage 8 —
  // ~3s after the result was already on screen.
  const resultRevealedFiredRef = useRef(null);
  useEffect(() => {
    let rid = null;
    if (pvpPhase === "resolution" && resolutionStage >= 8 && onlinePitchReveal) {
      rid = onlinePitchReveal.resolution_id;
    } else if (pvpPhase === "steal_resolution" && resolutionStage >= 4 && onlineStealResolution) {
      rid = onlineStealResolution.resolution_id;
    }
    if (!rid || resultRevealedFiredRef.current === rid) return;
    resultRevealedFiredRef.current = rid;
    if (onResultRevealed) onResultRevealed(rid);
  }, [pvpPhase, resolutionStage, onlinePitchReveal, onlineStealResolution, onResultRevealed]);

  // Request a server-authoritative pitch clock when entering pitch_select. Server returns the
  // existing clock if one is already active for this game, so both clients converge on the
  // same started_at. Suppressed while the half-inning hand-refresh modal is open — clock
  // starts only after both players have readied up and the modal has closed.
  //
  // Watchdog: a single dropped `request_clock` message (network blip, server briefly busy,
  // tab throttling at the moment of send) would previously strand the at-bat with no clock
  // until the next pvpPhase change — i.e. essentially forever. We retry every 5s while the
  // conditions hold and we still don't have a matching pitch clock. The handler is rate-
  // limit-exempt and idempotent, so retrying is harmless and self-stopping (the effect
  // re-runs and clears the interval the moment pitchClockSync arrives).
  useEffect(() => {
    if (!isOnline || !gameStarted || !wsSend || !wsConnected) return;
    if (gameOver) return;
    if (pvpPhase !== "pitch_select") return;
    if (handRefreshInFlight) return;
    if (bluffModalOpen || !opponentBluffReady) return;  // don't start clock until both sides have assigned bluff values
    // Treat an expired stale clock as missing — if the server's clear broadcast was lost,
    // the client could otherwise sit on a frozen 0:00 sync forever.
    const haveActivePitchClock =
      pitchClockSync &&
      pitchClockSync.kind === "pitch" &&
      pitchClockSync.started_at + pitchClockSync.duration * 1000 > Date.now();
    if (haveActivePitchClock) return;
    wsSend({ type: "request_clock", kind: "pitch" });
    const interval = setInterval(() => {
      wsSend({ type: "request_clock", kind: "pitch" });
    }, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, gameStarted, pvpPhase, pitchClockSync, wsConnected, handRefreshInFlight, gameOver, bluffModalOpen, opponentBluffReady]);

  // Server-synced pitch clock ticker. Both clients compute remaining from the same started_at,
  // so the displayed value matches across windows. Auto-action fires on expiry — independently
  // on each client, but at the same wall-clock instant since the math is identical.
  const autoActionedRef = useRef(false);
  useEffect(() => {
    if (!isOnline || !gameStarted || gameOver) {
      clearInterval(timerRef.current);
      setActionTimer(null);
      return;
    }
    const isClockPhase = pvpPhase === "pitch_select" || pvpPhase === "waiting_for_pitcher" || pvpPhase === "batter_select" || pvpPhase === "waiting_for_opponent" || pvpPhase === "steal_select" || pvpPhase === "steal_waiting";
    if (!isClockPhase || !pitchClockSync || handRefreshInFlight || bluffModalOpen || !opponentBluffReady) {
      clearInterval(timerRef.current);
      setActionTimer(null);
      autoActionedRef.current = false;
      return;
    }
    const computeRemaining = () => {
      const expiresAt = pitchClockSync.started_at + pitchClockSync.duration * 1000;
      return Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
    };
    // Auto-lock fallback chip. Deterministic on purpose: Math.random() made a
    // timed-out pitch impossible to reproduce from a bug report. Derived from the
    // clock start plus the current count so it still varies pitch to pitch.
    // `salt` keeps two derivations from colliding. Two collisions matter:
    //   * the two Tipping Pitches guesses — the server DISCARDS chip2 when it
    //     equals chip, so an unsalted second call silently voids the card;
    //   * pitcher vs batter — every seed input is broadcast identically to both
    //     clients, so after a pickoff/steal (which puts both roles on ONE shared
    //     clock) a double timeout would derive the same chip and hand the batter
    //     a guaranteed chip match.
    const autoPickChip = (salt = 0) => {
      const st = game?.state || {};
      if (!chipPool.length) return 1;
      const seed = Math.abs(
        (pitchClockSync.started_at | 0) + (st.balls || 0) * 7 + (st.strikes || 0) * 13
        + (st.outs || 0) * 31 + (st.inning || 0) * 57
        + (amIPitcher ? 0 : 3) + salt
      );
      return chipPool[seed % chipPool.length];
    };
    const tick = () => {
      const remaining = computeRemaining();
      setActionTimer(remaining);
      if (remaining !== 0 || autoActionedRef.current || !wsSend) return;
      // Auto-action on expiry. Latch via autoActionedRef so we only fire once per clock,
      // and only if we're actually in a "selecting" phase. The waiting_*/resolution phases
      // are server-driven and shouldn't auto-do anything from the client.
      if (pvpPhase === "pitch_select") {
        autoActionedRef.current = true;
        const myCurrentCards = amIPitcher ? pitcherPlayedCards : batterPlayedCards;
        const mX = HEAT_MAP_CONFIG.CHIP_RADIUS / HEAT_MAP_CONFIG.ZONE_WIDTH;
        const mY = HEAT_MAP_CONFIG.CHIP_RADIUS / HEAT_MAP_CONFIG.ZONE_HEIGHT;
        let finalChip, hmPayload = {};
        if (heatMap) {
          finalChip = heatMapChip ?? autoPickChip();
          if (!heatMapChip && onHeatMapChipChange) onHeatMapChipChange(finalChip);
          const autoPos = heatMapPos ?? { x: mX + Math.random() * (1 - 2 * mX), y: mY + Math.random() * (1 - 2 * mY) };
          if (!heatMapPos && onHeatMapPosChange) onHeatMapPosChange(autoPos);
          hmPayload = { heatmap_x: autoPos.x, heatmap_y: autoPos.y };
        } else {
          const myCurrentChip = amIPitcher ? pitcherChipPick : batterChipGuess;
          finalChip = myCurrentChip ?? autoPickChip();
          if (myCurrentChip === null) {
            if (amIPitcher) setPitcherChipPick(finalChip);
            else setBatterChipGuess(finalChip);
          }
        }
        if (!heatMap && (amIPitcher ? pitcherChipPick : batterChipGuess) === null) {
          toast.warning("Clock expired — a pitch was auto-selected for you");
        }
        const sent = wsSend({ type: "pitch_lock_in", pitch_token: pitchClockSync?.pitch_token, chip: finalChip, cards: myCurrentCards, ...hmPayload });
        // Only transition if the send succeeded; if the socket is closed the watchdog
        // will request a resync rather than leaving us stuck in waiting_for_opponent.
        if (sent) setPvpPhase("waiting_for_opponent");
      } else if (pvpPhase === "batter_select") {
        autoActionedRef.current = true;
        const mX = HEAT_MAP_CONFIG.CHIP_RADIUS / HEAT_MAP_CONFIG.ZONE_WIDTH;
        const mY = HEAT_MAP_CONFIG.CHIP_RADIUS / HEAT_MAP_CONFIG.ZONE_HEIGHT;
        let finalChip, finalChip2, hmPayload = {};
        if (heatMap) {
          finalChip = heatMapChip ?? autoPickChip();
          if (!heatMapChip && onHeatMapChipChange) onHeatMapChipChange(finalChip);
          const autoPos = heatMapPos ?? { x: mX + Math.random() * (1 - 2 * mX), y: mY + Math.random() * (1 - 2 * mY) };
          if (!heatMapPos && onHeatMapPosChange) onHeatMapPosChange(autoPos);
          hmPayload = { heatmap_x: autoPos.x, heatmap_y: autoPos.y };
        } else {
          finalChip = batterChipGuess ?? autoPickChip();
          if (batterChipGuess === null) setBatterChipGuess(finalChip);
          // Tipping Pitches: auto-select second chip if needed
          if (_allowTwoChips && batterChipGuess2 === null) {
            // Walk the salt until it differs; server requires chip2 !== chip.
            finalChip2 = autoPickChip(1);
            for (let _k = 2; finalChip2 === finalChip && _k < chipPool.length + 2; _k++) {
              finalChip2 = autoPickChip(_k);
            }
            setBatterChipGuess2(finalChip2);
          } else if (_allowTwoChips) {
            finalChip2 = batterChipGuess2;
          }
        }
        if (!heatMap && batterChipGuess === null) {
          toast.warning("Clock expired — a guess was auto-selected for you");
        }
        const chipPayload = _allowTwoChips ? { chip: finalChip, chip2: finalChip2 } : { chip: finalChip };
        const sent = wsSend({ type: "pitch_lock_in", pitch_token: pitchClockSync?.pitch_token, cards: batterPlayedCards, ...chipPayload, ...hmPayload });
        if (sent) setPvpPhase("waiting_for_opponent");
      } else if (pvpPhase === "steal_select") {
        // Mirror pitch_select: lock in whatever cards the player has selected (possibly
        // empty) so the steal resolves at exactly clock-zero instead of waiting for the
        // server's 2s grace failsafe. Server is idempotent on duplicate steal_lock_in
        // from the same role, so a near-simultaneous manual click is safe.
        autoActionedRef.current = true;
        const sent = wsSend({ type: "steal_lock_in", cards: stealCards });
        if (sent) setPvpPhase("steal_waiting");
      }
    };
    tick(); // immediate so display doesn't flash 60→59
    timerRef.current = setInterval(tick, 250); // 4Hz for smooth countdown
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, gameStarted, pvpPhase, pitchClockSync, pitcherChipPick, batterChipGuess, batterChipGuess2, heatMapChip, heatMapPos, pitcherPlayedCards, batterPlayedCards, stealCards, handRefreshInFlight, gameOver, bluffModalOpen, opponentBluffReady]);

  // Reset the auto-action latch whenever we get a fresh clock
  useEffect(() => { autoActionedRef.current = false; }, [pitchClockSync?.started_at]);

  // Heat Map auto-lock-in: GameBoard increments heatMapDropSeq each time the user finishes
  // a drag (pointer-up). We fire pitch_lock_in immediately so no button click is needed.
  // The ref pattern keeps the closure always fresh without re-creating the effect on every render.
  const _hmAutoLockFnRef = useRef(null);
  _hmAutoLockFnRef.current = () => {
    if (!isOnline || !heatMap || !wsSend || !wsConnected) return;
    if (pvpPhase !== "pitch_select" && pvpPhase !== "batter_select") return;
    if (heatMapChip === null || heatMapPos === null) return;
    if (handRefreshInFlight || gameOver) return;
    const chip = heatMapChip;
    const hmPayload = { heatmap_x: heatMapPos.x, heatmap_y: heatMapPos.y };
    const myCards = amIPitcher ? pitcherPlayedCards : batterPlayedCards;
    if (alreadySubmitted) return;   // already answered this pitch — see _submittedTokenRef
    const sent = wsSend({ type: "pitch_lock_in", pitch_token: pitchClockSync?.pitch_token, chip, cards: myCards, ...hmPayload });
    if (sent && _lockToken) _submittedTokenRef.current = _lockToken;
    if (sent) setPvpPhase("waiting_for_opponent");
    else toast.error("Connection lost — try again");
  };
  useEffect(() => {
    if (!heatMapDropSeq) return; // 0 = no drops yet this pitch
    _hmAutoLockFnRef.current?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heatMapDropSeq]);

  // All or Nothing: when the state flag is set both players skip chip selection entirely
  // and auto-lock-in with no chip. Fires as soon as pitch_select is entered with AON active.
  useEffect(() => {
    if (!isOnline || (pvpPhase !== "pitch_select" && pvpPhase !== "batter_select") || !wsSend || !wsConnected || gameOver) return;
    if (!game?.state?.pending_all_or_nothing) return;
    const sent = wsSend({ type: "pitch_lock_in", pitch_token: pitchClockSync?.pitch_token, chip: null, cards: [] });
    if (sent) setPvpPhase("waiting_for_opponent");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, pvpPhase, game?.state?.pending_all_or_nothing, wsConnected, gameOver]);

  // Publish current pitch clock value to GameBoard so the widget renders centrally.
  // Hide (null) outside select/waiting phases AND while the half-inning hand-refresh modal is
  // up — otherwise the widget bleeds through the modal's translucent backdrop.
  useEffect(() => {
    if (!onPitchClockUpdate) return;
    const isClockPhase = isOnline && (pvpPhase === "pitch_select" || pvpPhase === "waiting_for_pitcher" || pvpPhase === "batter_select" || pvpPhase === "waiting_for_opponent" || pvpPhase === "steal_select" || pvpPhase === "steal_waiting");
    onPitchClockUpdate(isClockPhase && !handRefreshInFlight && !game?.no_timers ? actionTimer : null);
  }, [actionTimer, pvpPhase, isOnline, onPitchClockUpdate, handRefreshInFlight, game?.no_timers]);

  // Clear the published clock when this component unmounts (e.g. transitioning to fielding)
  useEffect(() => () => { if (onPitchClockUpdate) onPitchClockUpdate(null); }, [onPitchClockUpdate]);

  // Sync pvpPhase up to GameBoard so it can show/hide HeatMapZone in the center field area.
  useEffect(() => { if (onPvpPhaseChange) onPvpPhaseChange(pvpPhase); }, [pvpPhase, onPvpPhaseChange]);

  // ── Stale-selection guard ───────────────────────────────────────────────────
  // A chip pick must never survive into the next pitch. clear() resets the picks,
  // but it only runs on the local-resolution paths (see its call sites) — when the
  // server drives us back into a selecting phase, whatever was picked last pitch is
  // still in state. The auto-lock then reads `myCurrentChip ?? random` and silently
  // resubmits it, which is how a playtester lost a whole at-bat to the same chip
  // being auto-guessed three pitches running.
  //
  // Keyed on the phase TRANSITION rather than the count, because a foul ball with two
  // strikes starts a new pitch without changing inning, half, batter, balls or strikes.
  const _appliedPhaseSyncRef = useRef(undefined);
  // ── Server-authoritative lock-in restore ────────────────────────────────────
  // phase_sync arrives after every request_state (fired on each reconnect and by the
  // Resync button). If the server still holds our pitch lock-in, we already submitted
  // — go back to waiting instead of showing a live Lock In button that would either
  // double-submit or leave both players waiting on each other.
  useEffect(() => {
    if (!isOnline) return;
    // Adopt-on-mount, then latch per message object.
    //   * adopt: on the first run take whatever is already in state WITHOUT acting on
    //     it. This component can remount mid-game, and a phase_sync built for an
    //     earlier play would otherwise be replayed onto the current one.
    //   * latch: FieldingPhase reads the same prop, so clearing it from here would
    //     race — whichever effect ran first would blank it for the other.
    if (_appliedPhaseSyncRef.current === undefined) {
      _appliedPhaseSyncRef.current = phaseSync;
      return;
    }
    if (!phaseSync) return;
    if (_appliedPhaseSyncRef.current === phaseSync) return;
    _appliedPhaseSyncRef.current = phaseSync;
    if (phaseSync.pitch_locked
        && (pvpPhase === "pitch_select" || pvpPhase === "batter_select"
            || pvpPhase === "waiting_for_pitcher")) {
      setPvpPhase("waiting_for_opponent");
      return;
    }
    // Self-heal a stranded batter.
    //
    // `pitcher_ready` is UNICAST to the batter and is the only live signal moving
    // waiting_for_pitcher -> batter_select. Lose that one frame — socket blip, a
    // remount mid-pitch, a race on connect — and the batter waits forever while the
    // pitcher sits in waiting_for_opponent. Two clients, same server seq,
    // contradictory phases, neither able to act.
    //
    // The server already tells us everything needed to recover: it has been sending
    // opponent_pitch_locked in every phase_sync and we were throwing it away, which
    // is why the Resync button could not repair this. Now it can.
    if (!phaseSync.pitch_locked && phaseSync.opponent_pitch_locked
        && amIBatter && pvpPhase === "waiting_for_pitcher") {
      setPvpPhase("batter_select");
      return;
    }
    // Our lock really was dropped: we believe we are waiting, the server has no
    // submission from us. Return to selection so the player can re-send.
    //
    // This is the recovery the old 28s watchdog performed BLINDLY — it reset the
    // phase whether or not the lock had actually been lost, which is what threw
    // away good locks. Same recovery, now conditioned on the server's answer.
    if (!phaseSync.pitch_locked && pvpPhase === "waiting_for_opponent") {
      setPvpPhase(amIBatter ? "batter_select" : "pitch_select");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseSync, isOnline, amIBatter]);

  const _prevSelectPhaseRef = useRef(null);
  useEffect(() => {
    const selecting = pvpPhase === "pitch_select" || pvpPhase === "batter_select";
    // waiting_for_pitcher counts as selecting: the batter can pre-pick a guess
    // there (chip row + keyboard both wired), and treating it as non-selecting
    // made the waiting_for_pitcher -> batter_select hand-off wipe that pick.
    const wasSelecting = _prevSelectPhaseRef.current === "pitch_select"
      || _prevSelectPhaseRef.current === "batter_select"
      || _prevSelectPhaseRef.current === "waiting_for_pitcher";
    _prevSelectPhaseRef.current = pvpPhase;
    if (selecting && !wasSelecting) {
      setPitcherChipPick(null);
      setBatterChipGuess(null);
      setBatterChipGuess2(null);
    }
  }, [pvpPhase]);

  const clear = useCallback(() => {
    setResult(null);
    setPitcherRoll(""); setPitcherManager(""); setPitcherChips("");
    setBatterRoll(""); setBatterManager(""); setBatterChips("");
    // Reset PvP state for next at-bat
    if (isOnline && myRole) {
      const isBotGame = !!game?.bot_role;
      setPvpPhase((!isBotGame && amIBatter) ? "waiting_for_pitcher" : "pitch_select");
    } else {
      setPvpPhase("pitcher_select");
    }
    setPitcherChipPick(null); setBatterChipGuess(null); setBatterChipGuess2(null);
    setPitcherPlayedCards([]); setBatterPlayedCards([]);
    setChipBonus(null);
    setPitcherPosition(null); setBatterPosition(null); setMyPosition(null);
    setRevealCountdown(null);
    setResolutionStage(0);
    setDiceDisplay({ p: 0, b: 0 });
    if (onClearPitchReveal) onClearPitchReveal();
  }, [isOnline, myRole, onClearPitchReveal, amIBatter, game?.bot_role]);

  useEffect(() => {
    if (!chipPanelDragging) return;
    const handleMove = (e) => {
      if (!chipPanelContainerRef.current) return;
      const rect = chipPanelContainerRef.current.getBoundingClientRect();
      const newW = e.clientX - rect.left;
      setChipPanelWidth(Math.min(Math.max(newW, 140), 420));
    };
    const handleUp = () => setChipPanelDragging(false);
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [chipPanelDragging]);

  // PvP: after batter hits Reveal
  const handleReveal = () => {
    const chipMatch = pitcherChipPick === batterChipGuess;
    let bonusObj;
    if (heatMap && pitcherPosition && batterPosition) {
      // Pixel-space distance so the non-square zone (280×360) doesn't produce an
      // elliptical dead-zone. Threshold = 2×CHIP_RADIUS (stroke edges touching).
      const dxPx = (pitcherPosition.x - batterPosition.x) * HEAT_MAP_CONFIG.ZONE_WIDTH;
      const dyPx = (pitcherPosition.y - batterPosition.y) * HEAT_MAP_CONFIG.ZONE_HEIGHT;
      const dist = Math.sqrt(dxPx * dxPx + dyPx * dyPx);
      const overlap = dist < 2 * HEAT_MAP_CONFIG.CHIP_RADIUS;
      const ob = game?.heat_map_overlap_bonus ?? 1;
      const cb = chipArsenal ? (chipArsenalModifiers[pitcherChipPick - 1] ?? 1) : pitchInventoryOn ? ([0, 0, 1, 2, 2][pitcherChipPick] ?? 0) : (game?.heat_map_chip_bonus ?? 1);
      const batterVal = (overlap ? ob : 0) + (chipMatch ? cb : 0);
      const pitcherVal = (overlap ? 0 : ob) + (chipMatch ? 0 : cb);
      const winner = batterVal >= pitcherVal ? "batter" : "pitcher";
      bonusObj = { winner, value: winner === "batter" ? batterVal : pitcherVal, pitcherValue: pitcherVal, batterValue: batterVal, overlap, chipMatch, isHeatMap: true };
    } else {
      const winner = chipMatch ? "batter" : "pitcher";
      let _localChipVal;
      if (chipArsenal) {
        _localChipVal = chipArsenalModifiers[pitcherChipPick - 1] ?? 1;
      } else if (pitchInventoryOn) {
        _localChipVal = [0, 0, 1, 2, 2][pitcherChipPick] ?? 0;
      } else {
        _localChipVal = game?.pitch_mod ?? 1;
      }
      bonusObj = { winner, value: _localChipVal, overlap: chipMatch, chipMatch };
    }
    setChipBonus(bonusObj);

    const cardById = (id) => [...pitchingHand, ...battingHand].find(c => c.id === id) || allCards.find(c => c.id === id);
    const played = [
      ...pitcherPlayedCards.map(id => ({ ...cardById(id), playedBy: "pitcher" })),
      ...batterPlayedCards.map(id => ({ ...cardById(id), playedBy: "batter" })),
    ].filter(c => c && c.id);
    if (onCardsPlayed) onCardsPlayed(played, pitchingTeamKey, battingTeamKey);

    setPvpPhase("reveal");
  };

  // PvP: after "Roll D10s" on reveal screen — pre-fill chip+card mods into chip/manager fields
  const handleStartRolling = () => {
    const cardById = (id) => [...pitchingHand, ...battingHand].find(c => c.id === id) || allCards.find(c => c.id === id);
    let pitcherCardMod = 0;
    let batterCardMod = 0;
    for (const id of pitcherPlayedCards) {
      const c = cardById(id);
      if (!c) continue;
      if (c.effect_type === "iconic_prime_pitcher" && currentPitcher) {
        const cats = c.condition?.categories || ["wins", "strikeouts", "era"];
        const lb = new Set(c.condition?.lower_is_better_categories || ["era"]);
        pitcherCardMod += Math.min(countPeakCategories(currentPitcher, cats, lb), c.max ?? 3);
      } else {
        pitcherCardMod += (c.pitcher_modifier || 0);
      }
    }
    for (const id of batterPlayedCards) {
      const c = cardById(id);
      if (!c) continue;
      if (c.effect_type === "iconic_prime_player" && currentBatter) {
        const cats = c.condition?.categories || ["hits", "doubles", "rbi"];
        batterCardMod += Math.min(countPeakCategories(currentBatter, cats), c.max ?? 3);
      } else {
        batterCardMod += (c.batter_modifier || 0);
      }
    }

    // Heat map mode: pitcher and batter each have independent bonuses
    if (heatMap && chipBonus?.isHeatMap) {
      const pTotal = (chipBonus.pitcherValue || 0) + pitcherCardMod;
      const bTotal = (chipBonus.batterValue || 0) + batterCardMod;
      setPitcherChips(pTotal ? String(pTotal) : "");
      setBatterChips(bTotal ? String(bTotal) : "");
    } else {
      const chipVal = chipBonus?.value || 0;
      if (chipBonus?.winner === "pitcher") {
        setPitcherChips(String(chipVal + pitcherCardMod));
        setBatterChips(batterCardMod ? String(batterCardMod) : "");
      } else {
        setBatterChips(String(chipVal + batterCardMod));
        setPitcherChips(pitcherCardMod ? String(pitcherCardMod) : "");
      }
    }
    setPvpPhase("rolling");
  };

  const rollBothDice = () => {
    setIsRolling(true); setResult(null);
    const animatePitcher = pitcherRoll === "";
    const animateBatter = batterRoll === "";

    rollInterval.current = setInterval(() => {
      if (animatePitcher) setPitcherRoll(String(Math.floor(Math.random() * 10)));
      if (animateBatter) setBatterRoll(String(Math.floor(Math.random() * 10)));
    }, 60);

    setTimeout(() => {
      clearInterval(rollInterval.current);
      const pRoll = animatePitcher ? Math.floor(Math.random() * 10) : parseInt(pitcherRoll) || 0;
      const bRoll = animateBatter ? Math.floor(Math.random() * 10) : parseInt(batterRoll) || 0;
      const pMgr = parseInt(pitcherManager) || 0;
      const pChip = parseInt(pitcherChips) || 0;
      const bMgr = parseInt(batterManager) || 0;
      const bChip = parseInt(batterChips) || 0;
      const pTotal = pRoll + pMgr + pChip;
      const bTotal = bRoll + bMgr + bChip;

      setPitcherRoll(String(pRoll));
      setBatterRoll(String(bRoll));
      setIsRolling(false);

      let res;
      if (pRoll === 0 && bRoll === 0) res = { type: "wild_pitch", label: "Wild Pitch", color: "bg-purple-600", desc: "Runners advance. +1 Ball." };
      else if (pRoll === 1 && bRoll === 1) res = { type: "hit_by_pitch", label: "Hit by Pitch", color: "bg-orange-600", desc: "Batter takes first!" };
      else if (pTotal === bTotal) res = { type: "foul", label: "Foul Ball", color: "bg-slate-600", desc: "Strike (unless 2)" };
      else if (pTotal > bTotal) {
        const isK = (game.state?.strikes || 0) >= 2;
        res = { type: "strike", label: isK ? "STRIKEOUT!" : "Strike", color: "bg-red-600", desc: isK ? "Batter strikes out!" : "Pitcher wins!" };
      }
      else {
        // Batter missed the pitch type (for require_chip_match_for_hr gate).
        // In heat map mode, HR gate reads type equality (chipMatch); in classic mode, pitcher
        // winning the chip match means batter lost — use chipBonus.winner directly instead
        // of comparing chip modifier values (which include card bonuses and break the check
        // when the batter loses chip match but played cards, making bChip > 0).
        const batterLostChip = heatMap
          ? !(chipBonus?.chipMatch)
          : (chipBonus?.winner === "pitcher");
        let contact = bTotal - pTotal;
        if (game.require_chip_match_for_hr && batterLostChip && contact > 6) contact = 6;
        let contactLabel, contactDesc;
        if (contact >= 7) { contactLabel = "CRUSHED!"; contactDesc = "Absolutely demolished!"; }
        else if (contact >= 5) { contactLabel = "Smashed!"; contactDesc = "Driven deep!"; }
        else if (contact >= 3) { contactLabel = "Solid Contact!"; contactDesc = "Good contact!"; }
        else if (contact >= 1) { contactLabel = "Weak Contact"; contactDesc = "Just got a piece of it"; }
        else { contactLabel = "Dribbler"; contactDesc = "Barely made contact"; }
        res = { type: "in_play", label: contactLabel, color: "bg-emerald-600", desc: contactDesc, contact };
      }

      setResult({ ...res, pitcherTotal: pTotal, batterTotal: bTotal, pRoll, bRoll, pMgr, pChip, bMgr, bChip });

      if (onLogPlay) {
        const batterName = currentBatter?.name?.split(' ').slice(1).join(' ') || 'Batter';
        const pitcherName = currentPitcher?.name?.split(' ').slice(1).join(' ') || 'Pitcher';
        const ctx = `(${pitcherName} ${pTotal} vs ${batterName} ${bTotal})`;
        if (res.type === 'strike') onLogPlay(`Strike — ${batterName} ${ctx}`);
        else if (res.type === 'foul') onLogPlay(`Foul Ball — ${batterName} ${ctx}`);
        else if (res.type === 'wild_pitch') onLogPlay(`Wild Pitch! +1 Ball`);
        else if (res.type === 'hit_by_pitch') onLogPlay(`Hit By Pitch — ${batterName} to 1B`);
        else if (res.type === 'in_play') onLogPlay(`${res.label}! ${batterName} — contact +${res.contact} ${ctx}`);
      }
      if (onChipsUsed) {
        onChipsUsed(currentPitcher?.id, currentBatter?.id, pMgr, pChip, bMgr, bChip);
      }
    }, 500);
  };

  const recordAndAdvance = async () => {
    if (!result || processing) return;
    if (isGameOver(game.state, game.innings_total || 9)) return;
    setProcessing(true);

    // Local mode: mark non-persistent cards as spent before clear() resets played-card state.
    // Persistent cards go to active_cards via the server; only non-persistent cards are spent here.
    // Called once per pitch regardless of result type (ball, strike, in_play, etc.).
    if (!isOnline && onSpendLocalCards) {
      const _cardById = (id) => [...pitchingHand, ...battingHand].find(c => c.id === id) || allCards.find(c => c.id === id);
      onSpendLocalCards({
        [pitchingTeamKey]: pitcherPlayedCards.filter(id => !_cardById(id)?.persistent),
        [battingTeamKey]: batterPlayedCards.filter(id => !_cardById(id)?.persistent),
      });
    }

    if (result.type === "in_play") {
      // Preserve the pitch resolution_id through to fielding → HomeRunHandler so an auto-HR
      // (contact >= 7) can apply state idempotently using the pitch resolution as its key.
      // Also preserve pre_bases (bases BEFORE server applied the HR) so the celebration toast
      // can count runs / name scoring runners — by the time HomeRunHandler renders, the server
      // has already cleared state.bases.
      setPitchResult({
        contact: result.contact,
        resolution_id: onlinePitchReveal?.resolution_id,
        pre_bases: onlinePitchReveal?.pre_bases || null,
        // Snapshot of the batter at the moment the pitch was thrown — used by
        // HomeRunHandler so the celebration toast names the actual hitter, not
        // the on-deck batter (state.X_batter_index has already advanced by then).
        pre_batter: onlinePitchReveal?.pre_batter || null,
        // Full chip + cards + dice breakdown so FieldingPhase can show the math
        // expression (e.g. "P 8+2 = 10  vs  B 9+2 = 11") that produced contact.
        pitcher_chip: onlinePitchReveal?.pitcher_chip,
        batter_chip: onlinePitchReveal?.batter_chip,
        batter_chip2: onlinePitchReveal?.batter_chip2,
        chip_winner: onlinePitchReveal?.chip_winner,
        chip_bonus: onlinePitchReveal?.chip_bonus,
        pitcher_cards: onlinePitchReveal?.pitcher_cards || [],
        batter_cards: onlinePitchReveal?.batter_cards || [],
        pitcher_card_mod: onlinePitchReveal?.pitcher_card_mod || 0,
        batter_card_mod: onlinePitchReveal?.batter_card_mod || 0,
        pitcher_d10: onlinePitchReveal?.pitcher_d10,
        batter_d10: onlinePitchReveal?.batter_d10,
        pitcher_total: onlinePitchReveal?.pitcher_total,
        batter_total: onlinePitchReveal?.batter_total,
        called_ball: !!onlinePitchReveal?.called_ball,
      });
      setCurrentPhase("fielding");
      clear();
      setProcessing(false);
      return;
    }
    // Online: server already mutated state and emitted play_recorded events when it broadcast
    // pitch_reveal. Client just animates and resets local UI — no PUT, no stat callbacks.
    if (isOnline) {
      clear();
      setProcessing(false);
      return;
    }
    if (result.type === "wild_pitch") { await handleWildPitch(); setProcessing(false); return; }
    if (result.type === "hit_by_pitch") { await handleHitByPitch(); setProcessing(false); return; }
    if (result.type === "ball") { await handleBall(); setProcessing(false); return; }
    const newState = { ...game.state };
    newState.inning_scores = ensureInningScores(game.state.inning_scores, Math.max(9, game.state.inning));
    // Chip Arsenal supply: decrement per-throw for non-infinite chips (local mode only).
    if (chipArsenal && pitcherChipPick != null && pitcherChipPick !== _caInfinite) {
      const _caDef = { 1: game.chip_arsenal_fb_count || 8, 2: game.chip_arsenal_ch_count || 5, 3: game.chip_arsenal_cb_count || 3, 4: game.chip_arsenal_sl_count || 2 };
      const _caCur = { ...(_caSupplyState || _caDef) };
      _caCur[pitcherChipPick] = Math.max(0, (_caCur[pitcherChipPick] ?? _caDef[pitcherChipPick] ?? 0) - 1);
      newState.chip_arsenal_supply = _caCur;
    }
    if (result.type === "foul" && newState.strikes >= 2) {
      toast.info("Foul ball — still 2 strikes");
      clear(); setProcessing(false); return;
    }
    if (result.type === "strike" || result.type === "foul") {
      newState.strikes = (newState.strikes || 0) + 1;
      if (newState.strikes >= 3) {
        newState.strikes = 0; newState.balls = 0; newState.outs = (newState.outs || 0) + 1;
        // No toast — the on-screen result badge already animates the strikeout.
        // The play-log entry below is the durable record.
        if (onRecordPlay) onRecordPlay(currentPitcher?.id, currentBatter?.id, { type: 'strikeout' });
        if (onLogPlay) onLogPlay(`STRIKEOUT! ${currentBatter?.name || 'Batter'}`);
        if (newState.outs >= 3) {
          const innIdx = game.state.inning - 1;
          if (newState.inning_scores[innIdx][game.state.half] === null) newState.inning_scores[innIdx][game.state.half] = 0;
          newState.outs = 0; newState.bases = { first: null, second: null, third: null };
          newState[getBatterIndexKey(game.state.half)] = nextBatterIndex(game.state, batters.length);
          newState.half = game.state.half === "top" ? "bottom" : "top";
          if (game.state.half === "bottom") newState.inning += 1;
          toast.info("Side retired!");
          onUpdate(newState); clear();
          setProcessing(false);
          if (onHalfInningEnd) onHalfInningEnd();
          return;
        } else { newState[getBatterIndexKey(game.state.half)] = nextBatterIndex(game.state, batters.length); }
      }
    }
    onUpdate(newState); clear();
    setProcessing(false);
  };

  const handleWildPitch = async () => {
    const newState = { ...game.state }; newState.inning_scores = ensureInningScores(game.state.inning_scores, Math.max(9, game.state.inning));
    newState.balls = (newState.balls || 0) + 1;
    const newBases = { first: null, second: null, third: null }; const scoringRunners = [];
    if (game.state.bases.third) { scoringRunners.push(game.state.bases.third.name || 'Runner'); if (game.state.half === "bottom") newState.home_score = (newState.home_score || 0) + 1; else newState.away_score = (newState.away_score || 0) + 1; }
    if (scoringRunners.length > 0) showScoringToast(scoringRunners, "Wild pitch!");
    if (game.state.bases.second) newBases.third = { ...game.state.bases.second };
    if (game.state.bases.first) newBases.second = { ...game.state.bases.first };
    if (scoringRunners.length > 0) { const innIdx = game.state.inning - 1; newState.inning_scores[innIdx][game.state.half] = (newState.inning_scores[innIdx][game.state.half] || 0) + scoringRunners.length; }
    if (newState.balls >= 4) {
      newBases.first = { name: currentBatter?.name, id: currentBatter?.id };
      newState.balls = 0; newState.strikes = 0;
      newState[getBatterIndexKey(game.state.half)] = nextBatterIndex(game.state, batters.length);
      toast.success(`${currentBatter?.name || 'Batter'} walks on wild pitch!`);
      if (onRecordPlay) onRecordPlay(currentPitcher?.id, currentBatter?.id, { type: 'walk', rbi: scoringRunners.length || 0 });
      if (onLogPlay) onLogPlay(`WALK! ${currentBatter?.name || 'Batter'} takes 1B (wild pitch)`);
    }
    newState.bases = newBases;
    if (newState.balls > 0 && newState.balls < 4) toast.info("Wild pitch!");
    onUpdate(newState); clear();
  };

  const handleHitByPitch = async () => {
    const newState = { ...game.state }; newState.inning_scores = ensureInningScores(game.state.inning_scores, Math.max(9, game.state.inning));
    const newBases = { ...game.state.bases }; const scoringRunners = [];
    if (newBases.first && newBases.second && newBases.third) {
      scoringRunners.push(newBases.third.name || 'Runner');
      if (game.state.half === "bottom") newState.home_score = (newState.home_score || 0) + 1; else newState.away_score = (newState.away_score || 0) + 1;
      newBases.third = { ...newBases.second }; newBases.second = { ...newBases.first };
    } else if (newBases.first && newBases.second) {
      newBases.third = { ...newBases.second }; newBases.second = { ...newBases.first };
    } else if (newBases.first) {
      newBases.second = { ...newBases.first };
    }
    if (scoringRunners.length > 0) showScoringToast(scoringRunners, "HBP!");
    newBases.first = { name: currentBatter?.name, id: currentBatter?.id };
    if (scoringRunners.length > 0) { const innIdx = game.state.inning - 1; newState.inning_scores[innIdx][game.state.half] = (newState.inning_scores[innIdx][game.state.half] || 0) + scoringRunners.length; }
    newState.bases = newBases; newState.balls = 0; newState.strikes = 0;
    newState[getBatterIndexKey(game.state.half)] = nextBatterIndex(game.state, batters.length);
    toast.success(`${currentBatter?.name || 'Batter'} HBP!`);
    onUpdate(newState); clear();
  };

  const callBall = () => {
    setIsRolling(true); setResult(null);
    const animatePitcher = pitcherRoll === "";
    const animateBatter = batterRoll === "";
    rollInterval.current = setInterval(() => {
      if (animatePitcher) setPitcherRoll(String(Math.floor(Math.random() * 10)));
      if (animateBatter) setBatterRoll(String(Math.floor(Math.random() * 10)));
    }, 60);
    setTimeout(() => {
      clearInterval(rollInterval.current);
      const pRoll = animatePitcher ? Math.floor(Math.random() * 10) : parseInt(pitcherRoll) || 0;
      const bRoll = animateBatter ? Math.floor(Math.random() * 10) : parseInt(batterRoll) || 0;
      // Pure D10 rolloff — all chips and manager cards are voided on a called ball.
      const pTotal = pRoll;
      const bTotal = bRoll;
      setPitcherRoll(String(pRoll)); setBatterRoll(String(bRoll)); setIsRolling(false);
      const batterName = currentBatter?.name?.split(' ').slice(1).join(' ') || 'Batter';
      const pitcherName = currentPitcher?.name?.split(' ').slice(1).join(' ') || 'Pitcher';
      let res;
      if (pTotal >= bTotal) { res = { type: "ball", label: "Ball", color: "bg-blue-600", desc: "Pitcher throws a ball" }; if (onLogPlay) onLogPlay(`Ball (${pitcherName} ${pTotal} ≥ ${batterName} ${bTotal})`); }
      else { res = { type: "strike", label: "Strike", color: "bg-red-600", desc: "Batter took a strike!" }; if (onLogPlay) onLogPlay(`Called Strike (${batterName} ${bTotal} > ${pitcherName} ${pTotal})`); }
      setResult({ ...res, pitcherTotal: pTotal, batterTotal: bTotal, pRoll, bRoll, pMgr: 0, pChip: 0, bMgr: 0, bChip: 0 });
    }, 500);
  };

  const handleBall = async () => {
    const newState = { ...game.state };
    newState.inning_scores = ensureInningScores(game.state.inning_scores, Math.max(9, game.state.inning));
    if (chipArsenal && pitcherChipPick != null && pitcherChipPick !== _caInfinite) {
      const _caDef = { 1: game.chip_arsenal_fb_count || 8, 2: game.chip_arsenal_ch_count || 5, 3: game.chip_arsenal_cb_count || 3, 4: game.chip_arsenal_sl_count || 2 };
      const _caCur = { ...(_caSupplyState || _caDef) };
      _caCur[pitcherChipPick] = Math.max(0, (_caCur[pitcherChipPick] ?? _caDef[pitcherChipPick] ?? 0) - 1);
      newState.chip_arsenal_supply = _caCur;
    }
    newState.balls = (newState.balls || 0) + 1;
    if (newState.balls >= 4) {
      const newBases = { ...game.state.bases }; const scoringRunners = [];
      if (newBases.first && newBases.second && newBases.third) {
        scoringRunners.push(newBases.third.name || 'Runner');
        if (game.state.half === "bottom") newState.home_score = (newState.home_score || 0) + 1;
        else newState.away_score = (newState.away_score || 0) + 1;
        newBases.third = { ...newBases.second }; newBases.second = { ...newBases.first };
      } else if (newBases.first && newBases.second) {
        newBases.third = { ...newBases.second }; newBases.second = { ...newBases.first };
      } else if (newBases.first) { newBases.second = { ...newBases.first }; }
      if (scoringRunners.length > 0) { showScoringToast(scoringRunners); const innIdx = game.state.inning - 1; newState.inning_scores[innIdx][game.state.half] = (newState.inning_scores[innIdx][game.state.half] || 0) + scoringRunners.length; }
      newBases.first = { name: currentBatter?.name, id: currentBatter?.id };
      newState.bases = newBases; newState.balls = 0; newState.strikes = 0;
      newState[getBatterIndexKey(game.state.half)] = nextBatterIndex(game.state, batters.length);
      if (onRecordPlay) onRecordPlay(currentPitcher?.id, currentBatter?.id, { type: 'walk', rbi: scoringRunners.length });
      if (onLogPlay) onLogPlay(`WALK! ${currentBatter?.name || 'Batter'} takes 1B`);
      toast.success(`${currentBatter?.name || 'Batter'} walks!`);
    } else { toast.info(`Ball ${newState.balls}`); }
    onUpdate(newState); clear();
  };

  const hasMods = result && (result.pMgr !== 0 || result.pChip !== 0 || result.bMgr !== 0 || result.bChip !== 0);

  function formatMath(roll, mgr, chip, total) {
    const parts = [roll];
    if (mgr > 0) parts.push(`+${mgr}`); else if (mgr < 0) parts.push(`${mgr}`);
    if (chip > 0) parts.push(`+${chip}`); else if (chip < 0) parts.push(`${chip}`);
    if (parts.length === 1) return String(total);
    return `${parts.join('')} = ${total}`;
  }

  // ── Online: steal lock-in (either side picks cards before the throw) ────
  if (isOnline && pvpPhase === "steal_select") {
    const steal = onlineStealActive || {};
    const baseLabel = steal.next_base === "home" ? "Home" : steal.next_base === "third" ? "3B" : "2B";
    const amIBatting = myRole === steal.batting_role;
    const myHand = amIBatting ? battingHand : pitchingHand;
    const mySpent = amIBatting ? battingSpent : pitchingSpent;
    const myActive = amIBatting ? battingActive : pitchingActive;
    const accent = amIBatting
      ? { text: "text-amber-400", border: "border-amber-500", bg: "bg-amber-500/20", btn: "bg-amber-500 hover:bg-amber-400 text-black" }
      : { text: "text-red-400", border: "border-red-500", bg: "bg-red-500/20", btn: "bg-red-600 hover:bg-red-500 text-white" };
    const runnerName = steal.runner?.name || "Runner";

    const handleStealLockIn = () => {
      if (!wsSend) return;
      const sent = wsSend({ type: "steal_lock_in", cards: stealCards });
      if (sent) {
        setPvpPhase("steal_waiting");
      } else {
        toast.error("Connection lost — try again");
      }
    };

    return (
      <motion.div data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-row items-stretch gap-0 overflow-y-hidden">
        {/* Left: steal context + lock-in */}
        <div className="flex flex-col justify-center gap-3 px-3 py-2 flex-shrink-0 w-52">
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-amber-400 uppercase tracking-widest animate-pulse">Steal Attempt</p>
            <p className="text-sm font-black text-white leading-tight">
              {runnerName} <span className="text-slate-500 font-normal">→</span> {baseLabel}
            </p>
            <p className="text-[10px] text-slate-500">
              {amIBatting ? "Play cards to support the runner." : "Play cards to gun them down."}
            </p>
            {onlineStealOpponentLocked && (
              <p className="text-[10px] text-emerald-400 font-bold">Opponent locked in ✓</p>
            )}
          </div>
          <button
            type="button"
            onClick={handleStealLockIn}
            disabled={!wsConnected}
            title={!wsConnected ? "Reconnecting…" : undefined}
            data-testid="lock-in"
            data-phase="steal_select"
            data-blocked-reason={!wsConnected ? "disconnected" : undefined}
            aria-label="Lock in steal response"
            className={`w-full px-2 py-2 rounded-lg ${accent.btn} font-black text-[12px] transition-all disabled:opacity-40`}>
            {!wsConnected ? "Reconnecting…" : (stealCards.length > 0 ? `Lock In ${stealCards.length} Card${stealCards.length === 1 ? "" : "s"} →` : "Lock In →")}
          </button>
        </div>

        {/* Right: card hand */}
        <div className="flex-1 min-w-0 min-h-0 overflow-y-hidden border-l border-slate-800 px-4 py-2">
          <ManagerCardHand
            cards={myHand}
            playedCardIds={stealCards}
            spentCardIds={mySpent}
            activeCards={myActive}
            onDeactivate={onDeactivateCard}
            onToggle={(id) => setStealCards(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
            maxPlays={myHand.length || 4}
            label="Your Hand"
            phase="steal"
            teamRole="batter"
            gameId={gameId}
            streakerInteractiveEligible={_streakerInteractiveEligible}
            unlimitedStacking={!!game?.unlimited_stacking}
            // Thread the active steal runner + base so canPlay can evaluate
            // steal_runner_career_sb conditions (Speed Boost) without us
            // changing the shape of `gameState`.
            gameState={{
              ...game.state,
              total_innings: game?.innings_total ?? 9,
              // Resolve the runner against the ROSTER, not the bases summary.
              // state.bases stores only {name, id}; steal_runner_career_sb calls
              // careerBestQualifyingFielder(runner, "stolen_bases"), which reads
              // runner.seasons and returns null for a summary — so every Steal
              // card evaluated as unplayable and a human could never click one.
              // The server already does this lookup (_steal_player_by_id), so
              // the cards worked fine once played; only the UI gate was wrong.
              _activeStealRunner: (() => {
                const rid = steal.runner?.id;
                if (!rid) return steal.runner || null;
                const rosters = [game?.home_team?.players, game?.away_team?.players];
                for (const players of rosters) {
                  const hit = (players || []).find(p => p && p.id === rid);
                  if (hit) return hit;
                }
                return steal.runner || null;
              })(),
              _activeStealBase: steal.base || null,
              _lastRollExists: !!(game?.last_roll_context),
              _onDeckBatter,
              _discard_count,
              _opponentActiveCards: _activeCount(pitchingTeamKey),
            }}
          />
        </div>
      </motion.div>
    );
  }

  // ── Online: waiting for opponent on a steal ─────────────────────────────
  if (isOnline && pvpPhase === "steal_waiting") {
    const steal = onlineStealActive || {};
    const baseLabel = steal.next_base === "home" ? "Home" : steal.next_base === "third" ? "3B" : "2B";
    return (
      <motion.div data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="h-full flex flex-col items-center justify-center gap-2">
        <div className="w-5 h-5 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
        <p className="text-xs text-slate-500">
          Steal <span className="font-bold text-amber-400">{baseLabel}</span> — waiting for opponent…
        </p>
        <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest">✓ You're locked in</p>
      </motion.div>
    );
  }

  // ── Online: steal resolution reveal (auto-dismisses after a beat) ───────
  if (isOnline && pvpPhase === "steal_resolution") {
    const r = onlineStealResolution || {};
    const baseLabel = r.next_base === "home" ? "Home" : r.next_base === "third" ? "3B" : "2B";
    const safe = r.outcome === "safe";
    const isRundown = r.outcome === "rundown";
    const cardById = (id) => [...pitchingHand, ...battingHand].find(c => c.id === id) || allCards.find(c => c.id === id);
    const battingCards = (r.batting_cards || []).map(id => cardById(id)).filter(Boolean);
    const fieldingCards = (r.fielding_cards || []).map(id => cardById(id)).filter(Boolean);
    const runnerName = r.runner?.name || "Runner";
    // Steal stage map (see resolution-progression effect): 1=cards, 2=dice spin, 3=dice land, 4=result, 5=advance
    const showCards = resolutionStage >= 1;
    const showDice = resolutionStage >= 2;
    const diceLanded = resolutionStage >= 3;
    const showResult = resolutionStage >= 4;

    return (
      <motion.div data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="h-full flex items-center justify-center gap-3 md:gap-6 px-3 overflow-hidden">
        {/* RUNNER (left) */}
        <motion.div initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
          className="flex flex-col items-center gap-1 min-w-0 flex-shrink-0">
          <p className="text-[9px] text-amber-400 font-bold uppercase tracking-widest">Runner</p>
          <span className="text-[11px] font-black text-amber-400 leading-none truncate max-w-[120px]">{runnerName}</span>
          {showDice && (
            <div className={`w-12 h-12 rounded-lg border-2 border-amber-500/60 bg-slate-900/80 flex items-center justify-center font-mono font-black text-2xl text-amber-400 ${!diceLanded ? "animate-pulse" : ""}`}>
              {diceLanded ? (pitcherDieAnimated ?? r.runner_d6) : diceDisplay.p}
            </div>
          )}
          {diceLanded && r.speed_mod !== 0 && (
            <span className="text-[9px] text-amber-400/70">+{r.speed_mod} speed</span>
          )}
          {showCards && battingCards.length > 0 && (
            <div className="flex flex-wrap gap-1 justify-center max-w-[120px]">
              {battingCards.map(c => (
                <span key={c.id} className="text-[8px] px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded text-amber-400 font-bold whitespace-nowrap">{c.name}</span>
              ))}
            </div>
          )}
        </motion.div>

        {/* CENTER — outcome */}
        <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
          className="flex flex-col items-center gap-1.5 min-w-0 flex-shrink-0">
          <div className="px-2 py-1 rounded-lg text-[10px] font-black whitespace-nowrap bg-slate-800 text-slate-400">
            Steal {baseLabel}
          </div>
          {diceLanded && (
            <div className="flex items-center gap-1.5 text-[11px] font-mono">
              <span className="text-amber-400 font-bold">{r.runner_total}</span>
              <span className="text-slate-600">vs</span>
              <span className="text-red-400 font-bold">{r.defender_total}</span>
            </div>
          )}
          {showResult && (
            <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.25 }}
              className={`${isRundown ? "bg-amber-600" : safe ? "bg-emerald-600" : "bg-rose-600"} rounded-lg py-1.5 px-4 text-white text-center whitespace-nowrap`}>
              <span className="text-sm font-black">{isRundown ? "RUNDOWN!" : safe ? "SAFE!" : "Caught Stealing"}</span>
            </motion.div>
          )}
        </motion.div>

        {/* DEFENDER (right) */}
        <motion.div initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
          className="flex flex-col items-center gap-1 min-w-0 flex-shrink-0">
          <p className="text-[9px] text-red-400 font-bold uppercase tracking-widest">Catcher</p>
          <span className="text-[11px] font-black text-red-400 leading-none">Throw</span>
          {showDice && (
            <div className={`w-12 h-12 rounded-lg border-2 border-red-500/60 bg-slate-900/80 flex items-center justify-center font-mono font-black text-2xl text-red-400 ${!diceLanded ? "animate-pulse" : ""}`}>
              {diceLanded ? (batterDieAnimated ?? r.defender_d6) : diceDisplay.b}
            </div>
          )}
          {diceLanded && r.fielding_mod !== 0 && (
            <span className="text-[9px] text-red-400/70">+{r.fielding_mod} field</span>
          )}
          {showCards && fieldingCards.length > 0 && (
            <div className="flex flex-wrap gap-1 justify-center max-w-[120px]">
              {fieldingCards.map(c => (
                <span key={c.id} className="text-[8px] px-1.5 py-0.5 bg-red-500/10 border border-red-500/30 rounded text-red-400 font-bold whitespace-nowrap">{c.name}</span>
              ))}
            </div>
          )}
        </motion.div>
      </motion.div>
    );
  }

  // ── Online: pick-off D10 animation ──────────────────────────────────────────
  // Shows for ~2 s after a pickoff card is played (both sides see it). Auto-dismisses back to pitch_select.
  if (isOnline && pvpPhase === "pickoff_roll") {
    const po = onlinePickoffRoll || {};
    const resultLabel = po.result === "out" ? "PICKED OFF!" : po.result === "passed_ball" ? "PASSED BALL" : "Dives Back!";
    const resultColor = po.result === "out" ? "text-red-400 bg-red-500/15 border-red-500/30"
      : po.result === "passed_ball" ? "text-amber-400 bg-amber-500/15 border-amber-500/30"
      : "text-emerald-400 bg-emerald-500/15 border-emerald-500/30";

    return (
      <motion.div data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="h-full flex flex-col items-center justify-center gap-3 px-4">
        {/* Card name chip */}
        <p className="text-[10px] font-bold text-red-400 uppercase tracking-widest">{po.card_name || "Pick-Off"}</p>

        {/* Single D10 die — red accent for the throw */}
        <div className="flex flex-col items-center gap-1">
          <span className="text-[9px] font-black text-red-500/70 tracking-widest uppercase">D10</span>
          <div className={`w-16 h-16 rounded-xl border-2 border-red-500/60 bg-slate-900/80 flex items-center justify-center font-mono font-black text-3xl text-red-300 ${!pickoffDiceLanded ? "animate-pulse" : ""}`}>
            {pickoffDiceLanded ? (po.roll ?? "?") : diceDisplay.p}
          </div>
        </div>
        <p className="text-[9px] text-slate-500">Need {po.threshold}+ to retire the runner</p>

        {/* Result badge */}
        <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.75, duration: 0.25 }}
          className={`px-4 py-1.5 rounded-lg border text-sm font-black ${resultColor}`}>
          {resultLabel}
        </motion.div>

        {/* Target base hint — selection happened before the roll */}
        {po.target_base && (
          <p className="text-[10px] text-slate-400">Target: {{ first: "1B", second: "2B", third: "3B" }[po.target_base] ?? po.target_base}</p>
        )}
        {po.result === "passed_ball" && po.runners_on?.length > 0 && (
          <p className="text-[10px] text-slate-500">All runners advance</p>
        )}
      </motion.div>
    );
  }

  // ── Online: pick-off target selection (BEFORE the roll) ─────────────────────
  // Fielding team clicks a highlighted runner tile on the diamond. Batting team sees
  // a waiting screen with a countdown. Once fielding team clicks (or 15s elapses),
  // server rolls the D10 and broadcasts pickoff_roll → both sides enter pickoff_roll.
  if (isOnline && pvpPhase === "pickoff_select") {
    const ps = onlinePickoffSelect || {};
    const amIFielding = myRole === (game.state.half === "top" ? "home" : "away");

    return (
      <motion.div data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="h-full flex flex-col items-center justify-center gap-3 px-4">
        <p className="text-[10px] font-bold text-red-400 uppercase tracking-widest">{ps.card_name || "Pick-Off"}</p>
        {amIFielding ? (
          // ── Fielding team: click a runner tile on the diamond ─────────────
          <>
            <p className="text-sm font-black text-white text-center">Click a runner on the diamond to target</p>
            <p className="text-[10px] text-slate-400">
              {pickoffSelectTimer !== null && pickoffSelectTimer > 0
                ? `${pickoffSelectTimer}s to select`
                : "Selecting…"}
            </p>
          </>
        ) : (
          // ── Batting team: waiting screen ──────────────────────────────────
          <>
            <div className="w-5 h-5 border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin" />
            <p className="text-xs text-slate-500">Opponent selecting pickoff target…</p>
            {pickoffSelectTimer !== null && pickoffSelectTimer > 0 && (
              <p className="text-[10px] text-slate-600">{pickoffSelectTimer}s</p>
            )}
          </>
        )}
      </motion.div>
    );
  }

  // ── Online: unified resolution (chips + cards + dice + result, one screen) ──
  if (isOnline && pvpPhase === "resolution") {
    const r = onlinePitchReveal || {};
    const cardById = (id) => [...pitchingHand, ...battingHand].find(c => c.id === id) || allCards.find(c => c.id === id);
    const pitcherCards = (r.pitcher_cards || []).map(id => cardById(id)).filter(Boolean);
    const batterCards = (r.batter_cards || []).map(id => cardById(id)).filter(Boolean);
    // Only show a card pill when that card actually contributed to the roll —
    // i.e. its name appears in the corresponding mod breakdown. This prevents
    // passive active-cards (rally_caps when not down 3+, career_game for wrong
    // batter, wrong-role leftovers) from cluttering the resolution display.
    const pitcherBdNames = new Set((r.pitcher_mod_breakdown || []).map(m => m.name));
    const batterBdNames = new Set((r.batter_mod_breakdown || []).map(m => m.name));
    const pitcherPills = pitcherCards.filter(c => pitcherBdNames.has(c.name));
    const batterPills = batterCards.filter(c => batterBdNames.has(c.name));
    const calledBall = !!r.called_ball;
    const chipMatch = r.chip_winner === "batter";
    const isBallInPlay = (r.triggered_effects || []).includes("ball_in_play");
    const showChips = resolutionStage >= 1;
    const showCards = resolutionStage >= 2;
    const showPitcherDice = !isBallInPlay && resolutionStage >= 3;
    const showBatterDice = resolutionStage >= 5;
    const showResult = resolutionStage >= 8;
    const showModReveal = resolutionStage >= 7;
    // Pre-pitch strike count, from the server's own snapshot. Reading
    // game.state here raced the game_state broadcast: if it had already
    // applied, strikes was POST-pitch, so a 1-2 count showed STRIKEOUT on
    // strike two, and a real strike three (count reset to 0) showed none.
    const preK = r.context?.strikes ?? game?.state?.strikes ?? 0;
    const labeled = resultLabelFor(r.result_type, r.contact || 0, preK);
    // Old Dog: resolve negated card names and open modal at result stage if 2+
    const odNegated = (r.old_dog_negated_cards || []).map(id => cardById(id)).filter(Boolean);
    if (showResult && odNegated.length >= 2 && oldDogModal === null) {
      setOldDogModal(odNegated);
    }

    // Mod-reveal running totals: die shows raw+accumulated mods during stage 7.
    // Modifiers reveal one at a time across both columns — the pitcher's stack
    // first (ticks 1..pLen), then the batter's (pLen+1..pLen+bLen) — so exactly
    // one die advances per beat. `isBallInPlay` zeroes the pitcher list to match
    // the reveal effect (BiP has no pitcher roll).
    const pitcherMods = isBallInPlay ? [] : buildPitchModList(r, "pitcher");
    const batterMods  = buildPitchModList(r, "batter");
    const revealedPMods = pitcherMods.slice(0, modRevealIndex);
    const revealedBMods = batterMods.slice(0, Math.max(0, modRevealIndex - pitcherMods.length));
    // During stage 7+ the die shows the ticking animated value (raw → total, one
    // integer at a time); before that it shows the spinning/landed raw roll.
    const pitcherDieValue = showModReveal
      ? (pitcherDieAnimated ?? (r.pitcher_d10 || 0))
      : diceDisplay.p;
    const batterDieValue = showModReveal
      ? (batterDieAnimated ?? (r.batter_d10 || 0))
      : diceDisplay.b;

    // Cap computation: capped mode stops the die display at raw die + highest card max.
    // Unlimited mode lets the die tick all the way to the server-computed total.
    const _pitchUnlimited = !!game?.unlimited_stacking;
    const _pCardsLookup = (r.pitcher_cards || []).map(id => cardById(id)).filter(Boolean);
    const _bCardsLookup = (r.batter_cards || []).map(id => cardById(id)).filter(Boolean);
    const _pCardMaxes = _pCardsLookup.map(c => c.max).filter(m => m != null && m > 0);
    const _bCardMaxes = _bCardsLookup.map(c => c.max).filter(m => m != null && m > 0);
    const _pitcherNatMax = r.pitcher_dice === "d20" ? 20 : 10;
    const _batterNatMax  = isBallInPlay ? 6 : (r.batter_dice === "d20" ? 20 : 10);
    const pitcherDieCap = _pCardMaxes.length > 0 ? (r.pitcher_d10 || 0) + Math.max(..._pCardMaxes) : _pitcherNatMax;
    const batterDieCap  = _bCardMaxes.length > 0 ? (r.batter_d10 || 0) + Math.max(..._bCardMaxes) : _batterNatMax;
    const pitcherDieDisplay = (!_pitchUnlimited && showModReveal) ? Math.min(pitcherDieValue, pitcherDieCap) : pitcherDieValue;
    const batterDieDisplay  = (!_pitchUnlimited && showModReveal) ? Math.min(batterDieValue, batterDieCap) : batterDieValue;

    // ── Calling Your Shot: single D20 box, no pitcher column ──
    const isCYS = r.result_type === "calling_your_shot_hr" || r.result_type === "calling_your_shot_out" || r.result_type === "calling_your_shot_strike";
    if (isCYS) {
      const showCYSDice = resolutionStage >= 5;
      const showCYSResult = resolutionStage >= 8;
      const cysCard = batterCards[0];
      return (
        <motion.div data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="h-full flex flex-col items-center justify-center gap-3 px-4">
          {cysCard && (
            <span className="text-[9px] px-2 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded text-amber-400 font-bold uppercase tracking-widest">
              {cysCard.name}
            </span>
          )}
          <p className="text-[10px] text-slate-500 uppercase tracking-widest">Calling Your Shot</p>
          {showCYSDice && (
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.2 }}
              className="flex flex-col items-center gap-1">
              <div className={`w-20 h-20 rounded-xl border-2 border-amber-500/60 bg-slate-900/80 flex items-center justify-center font-mono font-black text-4xl text-amber-400 ${resolutionStage === 5 ? "animate-pulse" : ""}`}>
                {diceDisplay.b}
              </div>
              <span className="text-[9px] font-black text-amber-400/60 tracking-widest uppercase">D20</span>
            </motion.div>
          )}
          {showCYSResult && (
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.25 }}
              className={`px-4 py-2 rounded-lg text-sm font-black uppercase tracking-widest ${labeled.color} text-white text-center`}>
              {labeled.label}
              {labeled.desc && <p className="text-[9px] font-normal normal-case tracking-normal mt-0.5 opacity-80">{labeled.desc}</p>}
            </motion.div>
          )}
        </motion.div>
      );
    }

    return (
      <motion.div data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="h-full flex items-center justify-center gap-3 md:gap-6 px-3 overflow-hidden">

        {/* Three-column chip/dice/result row */}
        <div className="flex items-center justify-center gap-3 md:gap-6">

        {/* PITCHER COLUMN (left) — hidden for Ball in Play */}
        {showChips && !isBallInPlay && (
          <motion.div initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
            className="flex flex-col items-center gap-1 min-w-0 flex-shrink-0">
            <p className="text-[9px] text-red-400 font-bold uppercase tracking-widest">Pitcher</p>
            <span className="text-lg font-black text-red-400 leading-none">{r.pitcher_chip != null ? (PITCH_TYPES[r.pitcher_chip - 1]?.abbr || "—") : "—"}</span>
            {!calledBall && _bluffValuesOn && r.pitcher_chip_value != null && (
              <span className="text-[10px] font-black text-red-400/70">val {r.pitcher_chip_value}</span>
            )}
            {showPitcherDice && (
              <div className="flex flex-col items-center gap-0.5">
                <div className={`w-12 h-12 rounded-lg border-2 border-red-500/60 bg-slate-900/80 flex items-center justify-center font-mono font-black text-2xl text-red-400 ${resolutionStage === 3 ? "animate-pulse" : ""}`}>
                  {pitcherDieDisplay}
                </div>
                {r.pitcher_dice === "d20" && <span className="text-[8px] font-black text-red-400/60 tracking-widest uppercase">D20</span>}
                {/* Max cap label — visible once die lands; mods animate/stack below it */}
                {resolutionStage >= 4 && (
                  <span className="text-[8px] text-slate-500 font-mono tabular-nums">max {pitcherDieCap}</span>
                )}
                {/* Mod reveal: each entry fades in at stage 7, die increments with it */}
                {showModReveal && revealedPMods.map((m, i) => (
                  <motion.span key={i} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
                    className={`text-[9px] font-bold ${m.value >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {m.name} {m.value > 0 ? "+" : ""}{m.value}
                  </motion.span>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* CENTER COLUMN — result label + totals (bonus mods now revealed per-column at stage 7) */}
        {showChips && (
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
            className="flex flex-col items-center gap-1.5 min-w-0 flex-shrink-0">
            {calledBall && (
              <div className="px-2 py-1 rounded-lg text-[10px] font-black whitespace-nowrap bg-blue-500/20 text-blue-300">
                Took the pitch
              </div>
            )}
            {showResult && (
              <>
                <div className="flex items-center gap-1.5 text-[11px] font-mono">
                  <span className="text-red-400 font-bold">
                    {pitcherMods.length === 0
                      ? r.pitcher_total
                      : `${r.pitcher_d10}${pitcherMods.map(m => m.value >= 0 ? `+${m.value}` : `${m.value}`).join('')}=${r.pitcher_total}`}
                  </span>
                  <span className="text-slate-600">vs</span>
                  <span className="text-amber-400 font-bold">
                    {batterMods.length === 0
                      ? r.batter_total
                      : `${r.batter_d10}${batterMods.map(m => m.value >= 0 ? `+${m.value}` : `${m.value}`).join('')}=${r.batter_total}`}
                  </span>
                </div>
                <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.25 }}
                  className={`${labeled.color} rounded-lg py-1.5 px-4 text-white text-center whitespace-nowrap`}>
                  <span className="text-sm font-black">{labeled.label}</span>
                  {/* Distance moved to the HomeRunHandler "💥 Home Run!" banner
                      (the next animation tab) per design — the result label
                      here just says "CRUSHED!" / "Smashed!" etc., not "Home
                      Run", so distance reads cleaner on the celebration. */}
                </motion.div>
                {(r.triggered_effects || []).length > 0 && (
                  <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.2 }}
                    className="flex flex-col items-center gap-0.5 mt-0.5">
                    {r.triggered_effects.map(fx => {
                      const odNames = odNegated.length === 1 ? odNegated[0]?.name : null;
                      const fxLabel = fx === "slap_hit_single_auto" ? "Slap Hit (auto) → SINGLE!"
                        : fx === "slap_hit_single" ? "Slap Hit triggered → SINGLE!"
                        : fx === "outcome_transform" ? "Card effect triggered!"
                        : fx === "old_dog_auto" ? (odNegated.length >= 2 ? `Old Dog fired — ${odNegated.length} cards negated` : `Old Dog fired — ${odNames || "card"} negated`)
                        : fx === "check_swing_fired" ? "Check Swing — strike converted to ball!"
                        : fx;
                      return (
                        <span key={fx} className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-0.5 whitespace-nowrap">
                          {fxLabel}
                        </span>
                      );
                    })}
                  </motion.div>
                )}
              </>
            )}
          </motion.div>
        )}

        {/* BATTER COLUMN (right) */}
        {showChips && (
          <motion.div initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
            className="flex flex-col items-center gap-1 min-w-0 flex-shrink-0">
            <p className="text-[9px] text-amber-400 font-bold uppercase tracking-widest">Batter</p>
            {calledBall && (
              <span className="text-[10px] font-black text-blue-300 leading-none uppercase tracking-widest">Take</span>
            )}
            <span className="text-lg font-black text-amber-400 leading-none">{calledBall ? "—" : (r.batter_chip != null ? (PITCH_TYPES[r.batter_chip - 1]?.abbr || "—") : "—")}</span>
            {!calledBall && _bluffValuesOn && r.batter_chip_value != null && (
              <span className="text-[10px] font-black text-amber-400/70">val {r.batter_chip_value}</span>
            )}
            {showBatterDice && (
              <div className="flex flex-col items-center gap-0.5">
                <div className={`w-12 h-12 rounded-lg border-2 border-amber-500/60 bg-slate-900/80 flex items-center justify-center font-mono font-black text-2xl text-amber-400 ${resolutionStage === 5 ? "animate-pulse" : ""}`}>
                  {batterDieDisplay}
                </div>
                {r.batter_dice === "d20" && <span className="text-[8px] font-black text-amber-400/60 tracking-widest uppercase">D20</span>}
                {isBallInPlay && <span className="text-[8px] font-black text-amber-400/60 tracking-widest uppercase">D6</span>}
                {/* Max cap label — visible once batter die lands; mods animate/stack below it */}
                {resolutionStage >= 6 && (
                  <span className="text-[8px] text-slate-500 font-mono tabular-nums">max {batterDieCap}</span>
                )}
                {showModReveal && revealedBMods.map((m, i) => (
                  <motion.span key={i} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
                    className={`text-[9px] font-bold ${m.value >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {m.name} {m.value > 0 ? "+" : ""}{m.value}
                  </motion.span>
                ))}
              </div>
            )}
          </motion.div>
        )}

        </div>{/* end three-column row */}

        {/* Old Dog multi-card modal — appears when 2+ opponent cards were negated */}
        {oldDogModal && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setOldDogModal(null)}>
            <div className="bg-slate-900 border border-amber-500/40 rounded-xl p-4 max-w-xs w-full mx-4 shadow-2xl"
              onClick={e => e.stopPropagation()}>
              <p className="text-xs font-black text-amber-400 mb-0.5">Old Dog fired</p>
              <p className="text-[10px] text-slate-400 mb-3">These opponent cards were negated this pitch:</p>
              <div className="flex flex-col gap-2">
                {oldDogModal.map(c => (
                  <div key={c.id} className="rounded-lg border border-slate-700/60 bg-slate-800/60 p-2">
                    <p className="text-[11px] font-black text-white">{c.name}</p>
                    {c.description && <p className="text-[10px] text-slate-400 mt-0.5 leading-snug">{c.description}</p>}
                  </div>
                ))}
              </div>
              <button onClick={() => setOldDogModal(null)}
                className="mt-3 w-full text-[10px] font-bold text-slate-400 hover:text-white transition-colors py-1">
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Read-only hand. This branch used to render NO hand at all, so the whole
            tray unmounted for the length of the resolution animation (20s+ with
            the half-inning modal). Two consequences, both reported from live
            games: a player could not see or plan their hand during the window,
            and a click aimed at a card was silently swallowed when the panel
            remounted underneath it — no error, no rejection, the card just never
            played. Keeping it mounted and inert fixes both, and keeps
            [data-testid^="card-"] continuously readable for automated seats.

            Visually hidden (sr-only): mounting is what fixes the bug, but the
            tray must not APPEAR here — that is how this screen looked before
            0.2.0 and how the owner wants it. sr-only rather than `hidden` or
            display:none, because those drop the node out of layout entirely and
            destroy the element stability this exists for. */}
        {isOnline && (amIBatter || amIPitcher) && (
          <div className="sr-only" aria-hidden="true" data-testid="card-tray-readonly" data-readonly="true" data-hidden-reason="restores-pre-0.2.0-lockin-ui">
            <ManagerCardHand
              readOnly
              cards={amIBatter ? battingHand : pitchingHand}
              playedCardIds={[]}
              spentCardIds={amIBatter ? battingSpent : pitchingSpent}
              activeCards={amIBatter ? battingActive : pitchingActive}
              maxPlays={0}
              label="Your Hand (resolving…)"
              phase="pitch"
              teamRole={amIBatter ? "batter" : "pitcher"}
              gameId={gameId}
              unlimitedStacking={!!game?.unlimited_stacking}
              gameState={{ ...(game?.state || {}), total_innings: game?.innings_total ?? 9 }}
              currentBatter={currentBatter}
              currentPitcher={currentPitcher}
              activeListeners={activeListeners?.[myRole] || []}
            />
          </div>
        )}
      </motion.div>
    );
  }

  // ── Online: Batter waiting for pitcher to select ────────────────────────
  if (isOnline && pvpPhase === "waiting_for_pitcher") {
    const chipCount = currentPitcher?.pitch_chips || 4;
    const myCards = batterPlayedCards;
    const setMyCards = setBatterPlayedCards;
    const visibleChips = chipArsenal ? [1, 2, 3, 4] : chipOrder.filter(n => n <= chipCount);

    // Keyboard: the batter can pre-select a guess while waiting on the pitcher.
    // No lock-in here — that button is a disabled placeholder in this screen.
    kbdActionsRef.current = {
      selectChip: (n) => {
        if (visibleChips.includes(n) && !isChipDepleted(n)) setBatterChipGuess(n);
      },
      lockIn: null, takePitch: null, walk: null,
    };

    return (
      <motion.div ref={chipPanelContainerRef} data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-row items-stretch">
        {!heatMap && (
          <>
            <div style={{ width: chipPanelWidth }} className="flex flex-col items-center justify-end gap-2 px-2 py-2 flex-shrink-0 overflow-hidden">
              {/* Status banner. Occupies the SAME box as batter_select's "Pitcher
                  played N cards" so the chip row below it never moves when the phase
                  flips. Doubles as the opponent sub-step indicator playtesters asked
                  for — previously this screen said nothing about what the pitcher was
                  doing. */}
              <div className="w-full px-2 py-1 rounded-md bg-slate-500/10 border border-slate-500/20 text-center"
                   data-testid="opponent-status">
                <p className="text-[10px] text-slate-400 font-bold">Pitcher is choosing…</p>
              </div>
              <div className="flex-1" />
              {/* Classic mode: pre-select chip while waiting */}
              <DndContext
                sensors={chipSensors}
                collisionDetection={closestCenter}
                onDragEnd={({ active, over }) => {
                  if (over && active.id !== over.id) {
                    setChipOrder(prev => {
                      const oldIdx = prev.indexOf(active.id);
                      const newIdx = prev.indexOf(over.id);
                      return arrayMove(prev, oldIdx, newIdx);
                    });
                  }
                }}
              >
                <SortableContext items={visibleChips} strategy={horizontalListSortingStrategy}>
                  <div className="flex items-center gap-2" data-testid="chip-row" data-phase="waiting_for_pitcher" data-role="batter" data-visible-chips={visibleChips.join(",")} data-chips-required={_allowTwoChips ? 2 : 1}>
                    {visibleChips.map(n => {
                      const pt = PITCH_TYPES[n - 1];
                      const _caLabel = chipArsenal ? `+${chipArsenalModifiers[n-1] ?? 1}${n === chipArsenalInfinite ? "∞" : ""}` : null;
                      return (
                        <SortableChip
                          key={n} n={n} pt={pt}
                          selected={batterChipGuess === n}
                          depleted={isChipDepleted(n)}
                          accent={{ text: "text-amber-400", border: "border-amber-500", bg: "bg-amber-500/20", btn: "bg-amber-500 hover:bg-amber-400 text-black" }}
                          onClick={() => {
                            // Same reducer as the batter_select row. This row
                            // only ever set slot 1, so under Tipping Pitches a
                            // pre-selection could not satisfy the requirement and
                            // reported data-chips-required={1} while the gate
                            // wanted 2.
                            const _n = nextChipSelection(
                              { chip1: batterChipGuess, chip2: batterChipGuess2 },
                              n,
                              _allowTwoChips ? 2 : 1,
                            );
                            setBatterChipGuess(_n.chip1);
                            setBatterChipGuess2(_n.chip2);
                          }}
                          bluffValue={_myChipValues ? (_myChipValues[pt.abbr] ?? null) : null}
                          bluffValueOwner={_myActivePitcher?.name || null}
                          chipValueLabel={_caLabel}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
              {/* Fixed-height caption slot — reserved even when empty so selecting a
                  chip does not nudge the buttons underneath it. */}
              <p className="text-[9px] text-slate-500 italic leading-none -mt-1 h-3">
                {batterChipGuess != null ? PITCH_TYPES[batterChipGuess - 1].name : "\u00a0"}
              </p>
              <div className="w-full space-y-1.5">
                <button disabled className="w-full px-2 py-1.5 rounded-md border border-blue-500/50 bg-blue-500/10 text-blue-300 font-bold text-[11px] opacity-40 cursor-not-allowed">
                  Take Pitch
                </button>
                <button disabled className="w-full px-2 py-2 rounded-lg bg-amber-500 text-black font-black text-[12px] opacity-40 cursor-not-allowed">
                  Lock in →
                </button>
              </div>
            </div>

            <div
              onMouseDown={(e) => { e.preventDefault(); setChipPanelDragging(true); }}
              className="w-1 cursor-ew-resize hover:bg-amber-500/50 transition-colors flex-shrink-0 bg-slate-800"
            />
          </>
        )}

        {heatMap && (
          <div style={{ width: chipPanelWidth }} className="flex flex-col items-end justify-end gap-2 px-2 py-2 flex-shrink-0 overflow-hidden">
            <div className="flex-1" />
            <div className="w-full space-y-1.5">
              <button disabled className="w-full px-2 py-1.5 rounded-md border border-blue-500/50 bg-blue-500/10 text-blue-300 font-bold text-[11px] opacity-40 cursor-not-allowed">
                Take Pitch
              </button>
              <button disabled className="w-full px-2 py-2 rounded-lg bg-amber-500 text-black font-black text-[12px] opacity-40 cursor-not-allowed">
                Lock in →
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 min-w-0 min-h-0 py-2 pr-3 relative">
          <ManagerCardHand
            cards={battingHand}
            playedCardIds={myCards}
            spentCardIds={battingSpent}
            activeCards={battingActive}
            onDeactivate={onDeactivateCard}
            onToggle={(id) => setMyCards(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
            maxPlays={battingHand.length || 4}
            label="Your Hand"
            phase="pitch"
            teamRole="batter"
            gameId={gameId}
            streakerInteractiveEligible={_streakerInteractiveEligible}
            unlimitedStacking={!!game?.unlimited_stacking}
            gameState={{ ...(game.state || {}), total_innings: game?.innings_total ?? 9, _reliever_available, _lastRollExists: !!(game?.last_roll_context), _onDeckBatter, _discard_count, _opponentActiveCards: _activeCount(myRole === "home" ? "away" : "home") }}
            currentBatter={currentBatter}
            currentPitcher={currentPitcher}
            oppActiveCards={(game?.[`${myRole === "home" ? "away" : "home"}_active_cards`] || []).filter(a => a && !a.burned)}
            lockedCardIds={Object.entries(game?.[`${myRole}_hand_locks`] || {}).filter(([, v]) => v > (game?.state?.inning || 1)).map(([k]) => k)}
            onInstantPlay={null}
            activeListeners={activeListeners[myRole] || []}
            onToggleListener={onToggleListener ? (id) => onToggleListener(id, myRole) : null}
          />
        </div>
      </motion.div>
    );
  }

  // ── Online: Batter's turn after pitcher has readied up ──────────────────
  if (isOnline && pvpPhase === "batter_select") {
    const chipCount = currentPitcher?.pitch_chips || 4;
    const myCards = batterPlayedCards;
    const setMyCards = setBatterPlayedCards;
    const pitcherCardCount = onlinePitcherReady?.cards_count ?? 0;

    const handleLockIn = () => {
      if (!wsSend) return;
      // Tipping Pitches requires two chip selections
      if (_allowTwoChips && (!batterChipGuess || !batterChipGuess2)) {
        toast.error("Select two pitch types");
        return;
      }
      const chip = heatMap ? heatMapChip : batterChipGuess;
      const hmPayload = heatMap && heatMapPos ? { heatmap_x: heatMapPos.x, heatmap_y: heatMapPos.y } : {};
      const chipPayload = _allowTwoChips ? { chip, chip2: batterChipGuess2 } : { chip };
      const sent = wsSend({ type: "pitch_lock_in", pitch_token: pitchClockSync?.pitch_token, cards: myCards, ...chipPayload, ...hmPayload });
      if (sent && _lockToken) _submittedTokenRef.current = _lockToken;
      if (sent) setPvpPhase("waiting_for_opponent");
      else toast.error("Connection lost — try again");
    };

    const handleCallBall = () => {
      if (!wsSend) return;
      const sent = wsSend({ type: "pitch_lock_in", pitch_token: pitchClockSync?.pitch_token, chip: null, cards: myCards, called_ball: true });
      if (sent) setPvpPhase("waiting_for_opponent");
      else toast.error("Connection lost — try again");
    };

    const visibleChips = chipArsenal ? [1, 2, 3, 4] : chipOrder.filter(n => n <= chipCount);

    // Keyboard: same bindings as pitch_select. In two-chip mode a repeat press
    // advances the same slot cycle the click handler uses, so keys and clicks
    // can be mixed freely.
    kbdActionsRef.current = {
      selectChip: (n) => {
        if (!visibleChips.includes(n) || isChipDepleted(n)) return;
        const sel1 = batterChipGuess === n;
        const sel2 = batterChipGuess2 === n;
        if (_allowTwoChips) {
          if (sel1 && sel2) { setBatterChipGuess(null); setBatterChipGuess2(null); }
          else if (sel1) setBatterChipGuess2(n);
          else if (sel2) setBatterChipGuess2(null);
          else setBatterChipGuess(n);
        } else {
          setBatterChipGuess(sel1 ? null : n);
        }
      },
      lockIn: ((_allowTwoChips ? (batterChipGuess === null || batterChipGuess2 === null) : (batterChipGuess === null))
        || !wsConnected || handRefreshInFlight) ? null : handleLockIn,
      takePitch: (wsConnected && !handRefreshInFlight) ? handleCallBall : null,
      walk: null,
    };

    return (
      <motion.div ref={chipPanelContainerRef} data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-row items-stretch">
        {/* ── LEFT COLUMN — pitcher card count + chip selector (classic) or action buttons only (heat map) */}
        <div style={{ width: chipPanelWidth }} className="flex flex-col items-center justify-end gap-2 px-2 py-2 flex-shrink-0 overflow-hidden">
          <div className="w-full px-2 py-1 rounded-md bg-red-500/10 border border-red-500/20 text-center">
            <p className="text-[10px] text-red-400 font-bold">
              {pitcherCardCount === 0
                ? "Pitcher played 0 cards"
                : `Pitcher playing ${pitcherCardCount} card${pitcherCardCount !== 1 ? "s" : ""}`}
            </p>
          </div>
          <div className="flex-1" />
          {/* Classic mode: chip selector */}
          {!heatMap && (
            <>
              <DndContext
                sensors={chipSensors}
                collisionDetection={closestCenter}
                onDragEnd={({ active, over }) => {
                  if (over && active.id !== over.id) {
                    setChipOrder(prev => {
                      const oldIdx = prev.indexOf(active.id);
                      const newIdx = prev.indexOf(over.id);
                      return arrayMove(prev, oldIdx, newIdx);
                    });
                  }
                }}
              >
                <SortableContext items={visibleChips} strategy={horizontalListSortingStrategy}>
                  <div className="flex items-center gap-2" data-testid="chip-row" data-phase="batter_select" data-role="batter" data-visible-chips={visibleChips.join(",")} data-chips-required={_allowTwoChips ? 2 : 1}>
                    {visibleChips.map(n => {
                      const pt = PITCH_TYPES[n - 1];
                      const _caLabel = chipArsenal ? `+${chipArsenalModifiers[n-1] ?? 1}${n === chipArsenalInfinite ? "∞" : ""}` : null;
                      const isSelected1 = batterChipGuess === n;
                      const isSelected2 = batterChipGuess2 === n;
                      const handleClick = () => {
                        // One reducer drives selection, and isSelectionComplete
                        // drives the Lock In gate, so what the row ALLOWS cannot
                        // drift from what the gate REQUIRES. It did: the gate and
                        // caption knew Tipping Pitches needs two chips while this
                        // handler had no branch for "a different chip while slot 1
                        // is full", so a second pick overwrote the first and the
                        // requirement was unsatisfiable — Lock In never enabled
                        // and the at-bat could not be submitted at all.
                        const _next = nextChipSelection(
                          { chip1: batterChipGuess, chip2: batterChipGuess2 },
                          n,
                          _allowTwoChips ? 2 : 1,
                        );
                        setBatterChipGuess(_next.chip1);
                        setBatterChipGuess2(_next.chip2);
                      };
                      return (
                        <SortableChip
                          key={n} n={n} pt={pt}
                          selected={isSelected1 || isSelected2}
                          slot={isSelected1 && isSelected2 ? "both" : isSelected1 ? "1" : isSelected2 ? "2" : null}
                          depleted={isChipDepleted(n)}
                          accent={{ text: "text-amber-400", border: "border-amber-500", bg: "bg-amber-500/20", btn: "bg-amber-500 hover:bg-amber-400 text-black" }}
                          onClick={handleClick}
                          bluffValue={_myChipValues ? (_myChipValues[pt.abbr] ?? null) : null}
                          bluffValueOwner={_myActivePitcher?.name || null}
                          chipValueLabel={_caLabel}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
              {_allowTwoChips ? (
                <div className="text-[9px] text-slate-500 italic leading-none -mt-1 h-3">
                  {batterChipGuess && batterChipGuess2 ? (
                    <p>{PITCH_TYPES[batterChipGuess - 1].abbr} & {PITCH_TYPES[batterChipGuess2 - 1].abbr}</p>
                  ) : batterChipGuess ? (
                    <p>{PITCH_TYPES[batterChipGuess - 1].name} + 1 more</p>
                  ) : (
                    <p>Select 2 pitches</p>
                  )}
                </div>
              ) : (
                <p className="text-[9px] text-slate-500 italic leading-none -mt-1 h-3">
                  {batterChipGuess != null ? PITCH_TYPES[batterChipGuess - 1].name : "\u00a0"}
                </p>
              )}
            </>
          )}
          {/* Action buttons — always in left column */}
          <div className="w-full space-y-1.5">
            <button type="button" onClick={handleCallBall} disabled={!wsConnected || handRefreshInFlight}
              title={!wsConnected ? "Reconnecting…" : "Take the pitch — straight d10 rolloff"}
              data-testid="take-pitch"
              data-phase="batter_select"
              data-blocked-reason={!wsConnected ? "disconnected" : handRefreshInFlight ? "hand-refresh" : undefined}
              aria-label="Take pitch — straight d10 rolloff"
              className="w-full px-2 py-1.5 rounded-md border border-blue-500/50 bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 font-bold text-[11px] transition-all disabled:opacity-40">
              Take Pitch
            </button>
            <button type="button" onClick={handleLockIn}
              disabled={(_allowTwoChips ? (batterChipGuess === null || batterChipGuess2 === null) : (batterChipGuess === null)) || !wsConnected || handRefreshInFlight}
              data-testid="lock-in"
              data-phase="batter_select"
              data-role="batter"
              data-chips-required={_allowTwoChips ? 2 : 1}
              data-blocked-reason={!wsConnected ? "disconnected"
                : handRefreshInFlight ? "hand-refresh"
                : (_allowTwoChips ? (batterChipGuess === null || batterChipGuess2 === null) : (batterChipGuess === null)) ? "no-chip-selected" : undefined}
              aria-label="Lock in"
              title={!wsConnected ? "Reconnecting…" : (_allowTwoChips ? (batterChipGuess === null || batterChipGuess2 === null ? "Select two pitch types" : undefined) : (batterChipGuess === null ? "Select a pitch type first" : undefined))}
              className="w-full px-2 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-black text-[12px] transition-all disabled:opacity-40">
              {!wsConnected ? "Reconnecting…" : "Lock in →"}
            </button>
          </div>
        </div>

        <div
          onMouseDown={(e) => { e.preventDefault(); setChipPanelDragging(true); }}
          className="w-1 cursor-ew-resize hover:bg-amber-500/50 transition-colors flex-shrink-0 bg-slate-800"
        />

        <div className="flex-1 min-w-0 min-h-0 py-2 pr-3">
          <ManagerCardHand
            cards={battingHand}
            playedCardIds={myCards}
            spentCardIds={battingSpent}
            activeCards={battingActive}
            onDeactivate={onDeactivateCard}
            onToggle={(id) => setMyCards(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
            maxPlays={battingHand.length || 4}
            label="Your Hand"
            phase="pitch"
            teamRole="batter"
            gameId={gameId}
            streakerInteractiveEligible={_streakerInteractiveEligible}
            unlimitedStacking={!!game?.unlimited_stacking}
            gameState={{ ...(game.state || {}), total_innings: game?.innings_total ?? 9, _reliever_available, _lastRollExists: !!(game?.last_roll_context), _onDeckBatter, _discard_count, _opponentActiveCards: _activeCount(myRole === "home" ? "away" : "home") }}
            currentBatter={currentBatter}
            currentPitcher={currentPitcher}
            oppActiveCards={(game?.[`${myRole === "home" ? "away" : "home"}_active_cards`] || []).filter(a => a && !a.burned)}
            lockedCardIds={Object.entries(game?.[`${myRole}_hand_locks`] || {}).filter(([, v]) => v > (game?.state?.inning || 1)).map(([k]) => k)}
            onInstantPlay={(id, extra = {}) => {
              if (!wsSend) return;
              const sent = wsSend({ type: "play_manager_card", card_id: id, ...extra });
              if (!sent) toast.error("Connection lost — try again");
              else if (onInstantCardPlayed) onInstantCardPlayed(id);
            }}
            activeListeners={activeListeners[myRole] || []}
            onToggleListener={onToggleListener ? (id) => onToggleListener(id, myRole) : null}
          />
        </div>
      </motion.div>
    );
  }

  if (isOnline && pvpPhase === "waiting_for_opponent") {
    const oppCardCount = amIPitcher
      ? (onlinePitcherLocked?.cards_count ?? null)
      : (onlinePitcherReady?.cards_count ?? null);
    const oppLabel = amIPitcher ? "Batter" : "Pitcher";

    return (
      <motion.div data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="h-full flex flex-col items-center justify-center gap-2">
        <div className="w-5 h-5 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
        {!wsConnected && (
          <p className="text-[10px] text-amber-400 animate-pulse font-bold">
            Connection lost — resuming when restored
          </p>
        )}
        <p className="text-xs text-slate-500">
          {amIPitcher
            ? <><span className="font-bold text-slate-300">{battingTeam.name}</span> batter is locking in…</>
            : "Waiting for resolution…"}
        </p>
        {oppCardCount != null && (
          <div className="px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/20 text-center">
            <p className="text-[10px] text-amber-400 font-bold">
              {oppCardCount === 0
                ? `${oppLabel} played 0 cards`
                : `${oppLabel} played ${oppCardCount} card${oppCardCount !== 1 ? "s" : ""}`}
            </p>
          </div>
        )}
        <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest">✓ You're locked in</p>
        {/* Same reasoning as the resolution branch: this seat has submitted and is
            waiting, which is a long window. Leaving the hand mounted (inert) keeps
            it readable and keeps element references stable across the transition
            into resolution, so a click is never aimed at a node that is about to
            be replaced. Visually hidden (sr-only) so the lock-in screen looks
            exactly as it did before 0.2.0 — mounting is what matters, not
            showing it. */}
        {isOnline && (amIBatter || amIPitcher) && (
          <div className="sr-only" aria-hidden="true" data-testid="card-tray-readonly" data-readonly="true" data-hidden-reason="restores-pre-0.2.0-lockin-ui">
            <ManagerCardHand
              readOnly
              cards={amIBatter ? battingHand : pitchingHand}
              playedCardIds={[]}
              spentCardIds={amIBatter ? battingSpent : pitchingSpent}
              activeCards={amIBatter ? battingActive : pitchingActive}
              maxPlays={0}
              label="Your Hand (locked in)"
              phase="pitch"
              teamRole={amIBatter ? "batter" : "pitcher"}
              gameId={gameId}
              unlimitedStacking={!!game?.unlimited_stacking}
              gameState={{ ...(game?.state || {}), total_innings: game?.innings_total ?? 9 }}
              currentBatter={currentBatter}
              currentPitcher={currentPitcher}
              activeListeners={activeListeners?.[myRole] || []}
            />
          </div>
        )}
      </motion.div>
    );
  }

  // ── Online: unified pitch select (both pitcher and batter use same UI) ──
  if (isOnline && pvpPhase === "pitch_select") {
    // All or Nothing active — auto-lock fires via useEffect; just show a holding screen.
    if (game?.state?.pending_all_or_nothing) {
      return (
        <motion.div data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-col items-center justify-center gap-3 px-4">
          <motion.div
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ repeat: Infinity, duration: 1.4, ease: "easeInOut" }}
            className="w-12 h-12 rounded-full bg-orange-500/20 border-2 border-orange-500/60 flex items-center justify-center text-xl"
          >
            🎲
          </motion.div>
          <p className="text-sm font-black uppercase tracking-widest text-orange-400">All or Nothing</p>
          <p className="text-[11px] text-slate-400 text-center">Straight D20 — no chips, no modifiers</p>
          <p className="text-[9px] text-slate-600 uppercase tracking-widest animate-pulse">Auto-locking in…</p>
        </motion.div>
      );
    }

    const chipCount = currentPitcher?.pitch_chips || 4;
    const myChip = amIPitcher ? pitcherChipPick : batterChipGuess;
    const setMyChip = amIPitcher ? setPitcherChipPick : setBatterChipGuess;
    const myCards = amIPitcher ? pitcherPlayedCards : batterPlayedCards;
    const setMyCards = amIPitcher ? setPitcherPlayedCards : setBatterPlayedCards;
    const myHand = amIPitcher ? pitchingHand : battingHand;
    const mySpent = amIPitcher ? pitchingSpent : battingSpent;
    const myActive = amIPitcher ? pitchingActive : battingActive;
    const myTeam = amIPitcher ? fieldingTeam : battingTeam;
    const accent = amIPitcher
      ? { text: "text-red-400", border: "border-red-500", bg: "bg-red-500/20", btn: "bg-red-600 hover:bg-red-500" }
      : { text: "text-amber-400", border: "border-amber-500", bg: "bg-amber-500/20", btn: "bg-amber-500 hover:bg-amber-400 text-black" };

    const handleLockIn = () => {
      if (!wsSend) return;
      const chip = heatMap ? heatMapChip : myChip;
      const hmPayload = heatMap && heatMapPos ? { heatmap_x: heatMapPos.x, heatmap_y: heatMapPos.y } : {};
      const sent = wsSend({
        type: "pitch_lock_in",
        chip,
        cards: myCards,
        ...hmPayload,
      });
      if (sent) {
        setPvpPhase("waiting_for_opponent");
      } else {
        toast.error("Connection lost — try again");
      }
    };

    // Batter-only: take the pitch (no chip). Cards still apply, but it's a straight d10 rolloff.
    const handleCallBall = () => {
      if (!wsSend) return;
      const sent = wsSend({
        type: "pitch_lock_in",
        chip: null,
        cards: myCards,
        called_ball: true,
      });
      if (sent) {
        setPvpPhase("waiting_for_opponent");
      } else {
        toast.error("Connection lost — try again");
      }
    };

    const handleIntentionalWalk = () => {
      if (!wsSend) return;
      const sent = wsSend({ type: "intentional_walk" });
      if (!sent) toast.error("Connection lost — try again");
    };

    const visibleChips = chipArsenal ? [1, 2, 3, 4] : chipOrder.filter(n => n <= chipCount);

    // Keyboard: 1-4 / F,C,B,S pick a chip, Enter locks in, T takes the pitch,
    // W issues the walk. Each action is published only when it is actually
    // legal, so a shortcut can never do something the button wouldn't.
    kbdActionsRef.current = {
      selectChip: heatMap ? null : (n) => {
        if (visibleChips.includes(n) && !isChipDepleted(n)) setMyChip(n);
      },
      lockIn: ((heatMap ? heatMapChip === null : myChip === null) || !wsConnected || handRefreshInFlight)
        ? null : handleLockIn,
      takePitch: (amIBatter && wsConnected && !handRefreshInFlight) ? handleCallBall : null,
      walk: (amIPitcher && wsConnected && !handRefreshInFlight) ? handleIntentionalWalk : null,
    };

    return (
      <motion.div ref={chipPanelContainerRef} data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-row items-stretch">
        {/* ── LEFT COLUMN — chip selector (classic) or action buttons only (heat map) */}
        <div style={{ width: chipPanelWidth }} className="flex flex-col items-center justify-end gap-2 px-2 py-2 flex-shrink-0 overflow-hidden">
          <div className="flex-1" />
          {/* Roll-override badge — persists until pitch resolves and state flag clears */}
          {game?.state?.pending_derby_swing && (
            <div className="w-full flex items-center gap-1.5 px-2 py-1 rounded-md bg-violet-900/40 border border-violet-600/50 text-violet-300 text-[10px]">
              <span>🎲</span>
              <span className="font-semibold">Derby Swing</span>
              <span className="text-violet-400/70">— D20 vs D10, no batter mods</span>
            </div>
          )}
          {/* Classic mode: chip selector */}
          {!heatMap && (
            <>
              <DndContext
                sensors={chipSensors}
                collisionDetection={closestCenter}
                onDragEnd={({ active, over }) => {
                  if (over && active.id !== over.id) {
                    setChipOrder(prev => {
                      const oldIdx = prev.indexOf(active.id);
                      const newIdx = prev.indexOf(over.id);
                      return arrayMove(prev, oldIdx, newIdx);
                    });
                  }
                }}
              >
                <SortableContext items={visibleChips} strategy={horizontalListSortingStrategy}>
                  <div className="flex items-center gap-2" data-testid="chip-row" data-phase="pitch_select" data-role={amIPitcher ? "pitcher" : "batter"} data-visible-chips={visibleChips.join(",")} data-chips-required={1}>
                    {visibleChips.map(n => {
                      const pt = PITCH_TYPES[n - 1];
                      const _caLabel = chipArsenal ? `+${chipArsenalModifiers[n-1] ?? 1}${n === chipArsenalInfinite ? "∞" : ""}` : null;
                      return (
                        <SortableChip
                          key={n}
                          n={n}
                          pt={pt}
                          selected={myChip === n}
                          depleted={isChipDepleted(n)}
                          accent={accent}
                          onClick={() => setMyChip(n)}
                          bluffValue={_myChipValues ? (_myChipValues[pt.abbr] ?? null) : null}
                          bluffValueOwner={_myActivePitcher?.name || null}
                          inventoryCount={
                            amIPitcher && pitchInventoryOn && n !== 1 ? _getRemaining(n) :
                            amIPitcher && chipArsenal && n !== _caInfinite ? _getCaRemaining(n) :
                            null
                          }
                          chipValueLabel={_caLabel ?? (pitchInventoryOn && amIPitcher ? _CHIP_VALUE_LABEL[n] : null)}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
              <p className="text-[9px] text-slate-500 italic leading-none -mt-1 h-3">
                {myChip != null ? PITCH_TYPES[myChip - 1].name : "\u00a0"}
              </p>
            </>
          )}
          {/* Action buttons — always in left column (heat map or classic) */}
          <div className="w-full space-y-1.5">
            {amIBatter && (
              <button
                type="button"
                onClick={handleCallBall}
                disabled={!wsConnected || handRefreshInFlight}
                title={!wsConnected ? "Reconnecting…" : "Take the pitch — straight d10 rolloff"}
                data-testid="take-pitch"
                data-phase="pitch_select"
                data-blocked-reason={!wsConnected ? "disconnected" : handRefreshInFlight ? "hand-refresh" : undefined}
                aria-label="Take pitch — straight d10 rolloff"
                className="w-full px-2 py-1.5 rounded-md border border-blue-500/50 bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 font-bold text-[11px] transition-all disabled:opacity-40">
                Take Pitch
              </button>
            )}
            {amIPitcher && (
              <button
                type="button"
                onClick={handleIntentionalWalk}
                disabled={!wsConnected || handRefreshInFlight}
                title={!wsConnected ? "Reconnecting…" : "Issue an intentional walk"}
                data-testid="intentional-walk"
                data-phase="pitch_select"
                data-blocked-reason={!wsConnected ? "disconnected" : handRefreshInFlight ? "hand-refresh" : undefined}
                aria-label="Issue an intentional walk"
                className="w-full px-2 py-1.5 rounded-md border border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 font-bold text-[11px] transition-all disabled:opacity-40">
                Walk
              </button>
            )}
            <button
              type="button"
              onClick={handleLockIn}
              disabled={(heatMap ? heatMapChip === null : myChip === null) || !wsConnected || handRefreshInFlight}
              title={!wsConnected ? "Reconnecting…" : (heatMap ? heatMapChip === null : myChip === null) ? "Select a pitch type first" : undefined}
              data-testid="lock-in"
              data-phase="pitch_select"
              data-role={amIPitcher ? "pitcher" : "batter"}
              data-blocked-reason={!wsConnected ? "disconnected"
                : handRefreshInFlight ? "hand-refresh"
                : (heatMap ? heatMapChip === null : myChip === null) ? "no-chip-selected" : undefined}
              aria-label="Lock in"
              className={`w-full px-2 py-2 rounded-lg ${accent.btn} font-black text-[12px] transition-all disabled:opacity-40 ${amIPitcher ? "text-white" : ""}`}>
              {!wsConnected ? "Reconnecting…" : "Lock in →"}
            </button>
          </div>
        </div>

        {/* DRAG HANDLE */}
        <div
          onMouseDown={(e) => { e.preventDefault(); setChipPanelDragging(true); }}
          className="w-1 cursor-ew-resize hover:bg-amber-500/50 transition-colors flex-shrink-0 bg-slate-800"
        />

        {/* ── RIGHT COLUMN (MANAGER CARDS) */}
        <div className="flex-1 min-w-0 min-h-0 py-2 pr-3">
          <ManagerCardHand
            cards={myHand}
            playedCardIds={myCards}
            spentCardIds={mySpent}
            activeCards={myActive}
            onDeactivate={onDeactivateCard}
            onToggle={(id) => setMyCards(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
            maxPlays={myHand.length || 4}
            label="Your Hand"
            phase="pitch"
            teamRole={amIBatter ? "batter" : "pitcher"}
            gameId={gameId}
            streakerInteractiveEligible={_streakerInteractiveEligible}
            unlimitedStacking={!!game?.unlimited_stacking}
            gameState={{ ...(game.state || {}), total_innings: game?.innings_total ?? 9, _reliever_available, _lastRollExists: !!(game?.last_roll_context), _onDeckBatter, _discard_count, _opponentActiveCards: _activeCount(myRole === "home" ? "away" : "home") }}
            currentBatter={currentBatter}
            currentPitcher={currentPitcher}
            oppActiveCards={(game?.[`${myRole === "home" ? "away" : "home"}_active_cards`] || []).filter(a => a && !a.burned)}
            lockedCardIds={Object.entries(game?.[`${myRole}_hand_locks`] || {}).filter(([, v]) => v > (game?.state?.inning || 1)).map(([k]) => k)}
            onInstantPlay={(id, extra = {}) => {
              if (id === "hack_the_board" && onHackTheBoardPlay) { onHackTheBoardPlay(id); return; }
              if (!wsSend) return;
              const card = [...(myHand || [])].find(c => c.id === id);
              const sent = wsSend({ type: "play_manager_card", card_id: id, ...extra });
              if (!sent) toast.error("Connection lost — try again");
              else {
                if (onInstantCardPlayed) onInstantCardPlayed(id);
                const card = [...pitchingHand, ...battingHand].find(c => c.id === id) || allCards.find(c => c.id === id);
                if (card && AUTO_AB_EFFECT_TYPES.has(card.effect_type) && onPitchClockUpdate) onPitchClockUpdate(null);
              }
            }}
            activeListeners={activeListeners[myRole] || []}
            onToggleListener={onToggleListener ? (id) => onToggleListener(id, myRole) : null}
          />
        </div>
      </motion.div>
    );
  }

  // Local pass-the-device PvP (pitcher_select → cover_to_batter → batter_select) removed.
  // Bot games and online H2H use the pitch_select / batter_select online path above.
  if (false && isPvP && !isOnline && pvpPhase === "pitcher_select") {
    const chipCount = currentPitcher?.pitch_chips || 4;
    if (heatMap) {
      return (
        <motion.div data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-row items-stretch gap-0 overflow-y-hidden">
          <div className="flex flex-col items-center gap-2 px-2 py-2 flex-shrink-0 overflow-y-auto">
            <p className="text-[10px] font-black text-red-400 uppercase tracking-widest leading-tight self-start">
              {fieldingTeam.name.split(' ').slice(-1)[0]} <span className="text-slate-500 font-normal normal-case">Pitcher</span>
            </p>
            <HeatMapZone
              role="pitcher"
              chipCount={chipCount}
              selectedChip={pitcherChipPick}
              onChipSelect={setPitcherChipPick}
              placedPosition={pitcherPosition}
              onPlace={setPitcherPosition}
              locked={false}
              revealMode={false}
              pitchHistory={pitcherHeatHistory}
              overhangThreshold={heatMapOverhangThreshold}
              zoneWidthPct={heatMapZoneWidthPct}
            />
            <button
              onClick={() => setPvpPhase("cover_to_batter")}
              disabled={pitcherChipPick === null || pitcherPosition === null}
              className="w-full px-3 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-black text-[12px] transition-all disabled:opacity-40">
              Hand Off →
            </button>
          </div>
          <div className="flex-1 min-w-0 min-h-0 overflow-y-hidden border-l border-slate-800 px-3 py-2">
            <ManagerCardHand
              cards={pitchingHand}
              playedCardIds={pitcherPlayedCards}
              spentCardIds={pitchingSpent}
              activeCards={pitchingActive}
              onDeactivate={onDeactivateCard}
              onToggle={(id) => setPitcherPlayedCards(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
              maxPlays={pitchingHand.length || 4}
              activeListeners={activeListeners[pitchingTeamKey] || []}
              onToggleListener={onToggleListener ? (id) => onToggleListener(id, pitchingTeamKey) : null}
              label="Your Hand"
              phase="pitch"
              teamRole="pitcher"
              gameId={gameId}
            streakerInteractiveEligible={_streakerInteractiveEligible}
            unlimitedStacking={!!game?.unlimited_stacking}
              gameState={{ ...(game.state || {}), total_innings: game?.innings_total ?? 9, _reliever_available, _lastRollExists: !!(game?.last_roll_context), _onDeckBatter, _discard_count, _opponentActiveCards: _activeCount(battingTeamKey) }}
              currentBatter={currentBatter}
              currentPitcher={currentPitcher}
              showSuggestions={false}
            />
          </div>
        </motion.div>
      );
    }
    return (
      <motion.div data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-row items-stretch gap-0 overflow-y-hidden">
        <div className="flex flex-col items-start justify-center gap-2 px-3 py-2 flex-shrink-0 w-32">
          <p className="text-[10px] font-black text-red-400 uppercase tracking-widest leading-tight">
            {fieldingTeam.name.split(' ').slice(-1)[0]}<br/>
            <span className="text-slate-500">Pitcher's Turn</span>
          </p>
          <div className="flex flex-col gap-1">
            <span className={`text-[10px] font-bold uppercase tracking-widest ${pitcherChipPick ? "text-slate-600" : "text-amber-400/80 animate-pulse"}`}>
              {pitcherChipPick ? "Pitch" : "↓ Select your pitch type"}
            </span>
            <div className={`flex flex-wrap gap-1.5 ${!pitcherChipPick ? "ring-1 ring-amber-500/30 rounded-lg p-0.5 animate-pulse" : ""}`}>
              {Array.from({ length: chipCount }, (_, i) => i + 1).map(n => {
                const pt = PITCH_TYPES[n - 1];
                const depleted = isChipDepleted(n);
                const _rem = _getRemaining(n);
                return (
                  <div key={n} className="relative">
                    <button onClick={depleted ? undefined : () => setPitcherChipPick(n)} title={depleted ? `${pt.name} (depleted)` : pt.name} disabled={depleted}
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-[11px] border-2 transition-all ${depleted ? "border-slate-800 bg-slate-900/60 text-slate-700 cursor-not-allowed line-through" : pitcherChipPick === n ? "border-red-500 bg-red-500/20 text-red-400 shadow-lg" : "border-slate-700 bg-slate-800/60 text-slate-400 hover:border-slate-500"}`}>
                      {pt.abbr}
                    </button>
                    {pitchInventoryOn && n !== 1 && _rem !== Infinity && (
                      <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[7px] font-black text-slate-400 bg-slate-900 border border-slate-700 rounded px-0.5 pointer-events-none leading-tight">
                        ×{_rem}
                      </span>
                    )}
                    {pitchInventoryOn && (
                      <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[7px] font-black text-emerald-400 bg-slate-900 rounded px-0.5 pointer-events-none leading-tight">
                        {_CHIP_VALUE_LABEL[n]}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {pitcherChipPick && <span className="text-[9px] text-slate-600 italic">{PITCH_TYPES[pitcherChipPick - 1].name} ✓</span>}
          </div>
          <button
            onClick={() => setPvpPhase("cover_to_batter")}
            disabled={pitcherChipPick === null}
            className="w-full px-2 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 text-white font-black text-[11px] transition-all disabled:opacity-40">
            Hand Off →
          </button>
        </div>
        <div className="flex-1 min-w-0 min-h-0 overflow-y-hidden border-l border-slate-800 px-3 py-2">
          <ManagerCardHand
            cards={pitchingHand}
            playedCardIds={pitcherPlayedCards}
            spentCardIds={pitchingSpent}
            activeCards={pitchingActive}
            onDeactivate={onDeactivateCard}
            onToggle={(id) => setPitcherPlayedCards(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
            maxPlays={pitchingHand.length || 4}
            activeListeners={activeListeners[pitchingTeamKey] || []}
            onToggleListener={onToggleListener ? (id) => onToggleListener(id, pitchingTeamKey) : null}
            label="Your Hand"
            phase="pitch"
            teamRole="pitcher"
            gameId={gameId}
            streakerInteractiveEligible={_streakerInteractiveEligible}
            unlimitedStacking={!!game?.unlimited_stacking}
            gameState={{ ...(game.state || {}), total_innings: game?.innings_total ?? 9, _reliever_available, _lastRollExists: !!(game?.last_roll_context), _onDeckBatter, _discard_count, _opponentActiveCards: _activeCount(battingTeamKey) }}
            currentBatter={currentBatter}
            currentPitcher={currentPitcher}
            showSuggestions={false}
          />
        </div>
      </motion.div>
    );
  }

  if (false && isPvP && !isOnline && pvpPhase === "cover_to_batter") {
    return (
      <>
        <motion.div data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full" />
        {/* Full-screen overlay to hide pitcher's choices */}
        <div className="fixed inset-0 z-[9999] bg-slate-950 flex flex-col items-center justify-center gap-6 px-6">
          <div className="w-16 h-16 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-2xl">⚾</div>
          <div className="text-center">
            <p className="text-xl font-black text-white">Pass the device</p>
            <p className="text-slate-400 text-sm mt-1">
              Hand off to <span className="text-amber-400 font-bold">{battingTeam.name}</span> — batter's turn.
            </p>
          </div>
          <button onClick={() => setPvpPhase("batter_select")}
            className="px-8 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-black text-sm transition-all">
            I'm Ready →
          </button>
        </div>
      </>
    );
  }

  if (false && isPvP && !isOnline && pvpPhase === "batter_select") {
    const chipCount = currentPitcher?.pitch_chips || 4;
    const localPitcherCardCount = pitcherPlayedCards.length;
    if (heatMap) {
      return (
        <motion.div data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-row items-stretch gap-0 overflow-y-hidden">
          <div className="flex flex-col items-center gap-2 px-2 py-2 flex-shrink-0 overflow-y-auto">
            <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest leading-tight self-start">
              {battingTeam.name.split(' ').slice(-1)[0]} <span className="text-slate-500 font-normal normal-case">Batter</span>
            </p>
            {localPitcherCardCount > 0 && (
              <div className="w-full px-1.5 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-center">
                <p className="text-[10px] text-red-400 font-bold">
                  Pitcher played {localPitcherCardCount} card{localPitcherCardCount !== 1 ? "s" : ""}
                </p>
              </div>
            )}
            <HeatMapZone
              role="batter"
              chipCount={chipCount}
              selectedChip={batterChipGuess}
              onChipSelect={setBatterChipGuess}
              placedPosition={batterPosition}
              onPlace={setBatterPosition}
              locked={false}
              revealMode={false}
              pitchHistory={pitcherHeatHistory}
            />
            <button
              onClick={handleReveal}
              disabled={batterChipGuess === null || batterPosition === null}
              className="w-full px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-black text-[12px] transition-all disabled:opacity-40">
              Reveal →
            </button>
          </div>
          <div className="flex-1 min-w-0 min-h-0 overflow-y-hidden border-l border-slate-800 px-3 py-2">
            <ManagerCardHand
              cards={battingHand}
              playedCardIds={batterPlayedCards}
              spentCardIds={battingSpent}
              activeCards={battingActive}
              onDeactivate={onDeactivateCard}
              onToggle={(id) => setBatterPlayedCards(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
              maxPlays={battingHand.length || 4}
              label="Your Hand"
              phase="pitch"
              teamRole="batter"
              gameId={gameId}
            streakerInteractiveEligible={_streakerInteractiveEligible}
            unlimitedStacking={!!game?.unlimited_stacking}
              gameState={{ ...(game.state || {}), total_innings: game?.innings_total ?? 9, _lastRollExists: !!(game?.last_roll_context), _onDeckBatter, _discard_count, _opponentActiveCards: _activeCount(pitchingTeamKey) }}
              currentBatter={currentBatter}
              currentPitcher={currentPitcher}
              showSuggestions={false}
              activeListeners={activeListeners[battingTeamKey] || []}
              onToggleListener={onToggleListener ? (id) => onToggleListener(id, battingTeamKey) : null}
            />
          </div>
        </motion.div>
      );
    }
    return (
      <motion.div data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-row items-stretch gap-0 overflow-y-hidden">
        <div className="flex flex-col items-start justify-center gap-2 px-3 py-2 flex-shrink-0 w-32">
          <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest leading-tight">
            {battingTeam.name.split(' ').slice(-1)[0]}<br/>
            <span className="text-slate-500">Batter's Turn</span>
          </p>
          {localPitcherCardCount > 0 && (
            <div className="w-full px-1.5 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-center">
              <p className="text-[10px] text-red-400 font-bold">
                Pitcher played {localPitcherCardCount} card{localPitcherCardCount !== 1 ? "s" : ""}
              </p>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <span className={`text-[10px] font-bold uppercase tracking-widest ${batterChipGuess ? "text-slate-600" : "text-amber-400/80 animate-pulse"}`}>
              {batterChipGuess ? "Guess" : "↓ Select your pitch type"}
            </span>
            <div className={`flex flex-wrap gap-1.5 ${!batterChipGuess ? "ring-1 ring-amber-500/30 rounded-lg p-0.5 animate-pulse" : ""}`}>
              {Array.from({ length: chipCount }, (_, i) => i + 1).map(n => {
                const pt = PITCH_TYPES[n - 1];
                const depleted = isChipDepleted(n);
                return (
                  <button key={n} onClick={depleted ? undefined : () => setBatterChipGuess(n)} title={depleted ? `${pt.name} (depleted)` : pt.name} disabled={depleted}
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-[11px] border-2 transition-all ${depleted ? "border-slate-800 bg-slate-900/60 text-slate-700 cursor-not-allowed line-through" : batterChipGuess === n ? "border-amber-500 bg-amber-500/20 text-amber-400 shadow-lg" : "border-slate-700 bg-slate-800/60 text-slate-400 hover:border-slate-500"}`}>
                    {pt.abbr}
                  </button>
                );
              })}
            </div>
            {batterChipGuess && <span className="text-[9px] text-slate-600 italic">guessing {PITCH_TYPES[batterChipGuess - 1].name}</span>}
          </div>
          <button
            onClick={handleReveal}
            disabled={batterChipGuess === null}
            className="w-full px-2 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-black text-[11px] transition-all disabled:opacity-40">
            Reveal →
          </button>
        </div>
        <div className="flex-1 min-w-0 min-h-0 overflow-y-hidden border-l border-slate-800 px-3 py-2">
          <ManagerCardHand
            cards={battingHand}
            playedCardIds={batterPlayedCards}
            spentCardIds={battingSpent}
            activeCards={battingActive}
            onDeactivate={onDeactivateCard}
            onToggle={(id) => setBatterPlayedCards(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
            maxPlays={battingHand.length || 4}
            label="Your Hand"
            phase="pitch"
            teamRole="batter"
            gameId={gameId}
            streakerInteractiveEligible={_streakerInteractiveEligible}
            unlimitedStacking={!!game?.unlimited_stacking}
            gameState={{ ...(game.state || {}), total_innings: game?.innings_total ?? 9, _lastRollExists: !!(game?.last_roll_context), _onDeckBatter, _discard_count, _opponentActiveCards: _activeCount(pitchingTeamKey) }}
            currentBatter={currentBatter}
            currentPitcher={currentPitcher}
            showSuggestions={false}
            activeListeners={activeListeners[battingTeamKey] || []}
            onToggleListener={onToggleListener ? (id) => onToggleListener(id, battingTeamKey) : null}
          />
        </div>
      </motion.div>
    );
  }

  // ── PvP: Reveal Screen ───────────────────────────────────────────────────
  if (isPvP && pvpPhase === "reveal") {
    const chipMatch = pitcherChipPick === batterChipGuess;
    const cardById = (id) => [...pitchingHand, ...battingHand].find(c => c.id === id) || allCards.find(c => c.id === id);
    const pitcherCards = pitcherPlayedCards.map(id => cardById(id)).filter(Boolean);
    const batterCards = batterPlayedCards.map(id => cardById(id)).filter(Boolean);

    return (
      <motion.div data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-col items-center justify-center gap-3 px-3 overflow-y-auto">
        {/* Chip reveal — pitcher / batter chips render "—" when missing instead
            of silently falling back to FB. The bonus pill only renders when the
            server actually awarded a bonus (chip_winner set + value > 0); on a
            called ball there's no chip game so the pill stays hidden. */}
        {!heatMap && (
          <div className="flex items-center gap-3">
            <div className="text-center">
              <p className="text-[9px] text-red-400 font-bold uppercase">Pitcher threw</p>
              <span className="text-xl font-black text-red-400">{pitcherChipPick != null ? (PITCH_TYPES[pitcherChipPick - 1]?.abbr || "—") : "—"}</span>
              <p className="text-[9px] text-red-400/60">{pitcherChipPick != null ? (PITCH_TYPES[pitcherChipPick - 1]?.name || "—") : "—"}</p>
            </div>
            <span className="text-slate-600 text-sm">vs</span>
            <div className="text-center">
              <p className="text-[9px] text-amber-400 font-bold uppercase">Batter guessed</p>
              <div className="flex items-center justify-center gap-1">
                <span className="text-xl font-black text-amber-400">{batterChipGuess != null ? (PITCH_TYPES[batterChipGuess - 1]?.abbr || "—") : "—"}</span>
                {batterChipGuess2 != null && (
                  <>
                    <span className="text-xs text-slate-600">+</span>
                    <span className="text-xl font-black text-amber-400">{PITCH_TYPES[batterChipGuess2 - 1]?.abbr || "—"}</span>
                  </>
                )}
              </div>
              <p className="text-[9px] text-amber-400/60">
                {batterChipGuess != null ? (PITCH_TYPES[batterChipGuess - 1]?.name || "—") : "—"}
                {batterChipGuess2 != null && ` + ${PITCH_TYPES[batterChipGuess2 - 1]?.name || "—"}`}
              </p>
            </div>
            {chipBonus && chipBonus.winner && chipBonus.value > 0 && (
              <div className={`ml-1 px-2 py-1 rounded-lg text-[10px] font-black ${chipMatch ? "bg-amber-500/20 text-amber-400" : "bg-red-500/20 text-red-400"}`}>
                {chipMatch ? `Batter +${chipBonus.value}` : `Pitcher +${chipBonus.value}`}
              </div>
            )}
          </div>
        )}

        {/* Heat Map reveal zone — shows both chip positions overlaid */}
        {heatMap && (
          <HeatMapZone
            revealMode={true}
            pitcherPosition={pitcherPosition}
            batterPosition={batterPosition}
            pitcherChip={pitcherChipPick}
            batterChip={batterChipGuess}
            positionalOverlap={chipBonus?.overlap ?? null}
            typeMatch={chipBonus?.chipMatch ?? null}
            pitchHistory={pitcherHeatHistory}
          />
        )}

        {/* Heat Map dual-bonus summary */}
        {heatMap && chipBonus?.isHeatMap && (
          <div className="flex gap-3">
            {(chipBonus.pitcherValue ?? 0) > 0 && (
              <div className="px-2 py-1 rounded-lg text-[10px] font-black bg-red-500/20 text-red-400">
                Pitcher +{chipBonus.pitcherValue}
              </div>
            )}
            {(chipBonus.batterValue ?? 0) > 0 && (
              <div className="px-2 py-1 rounded-lg text-[10px] font-black bg-amber-500/20 text-amber-400">
                Batter +{chipBonus.batterValue}
              </div>
            )}
          </div>
        )}

        {/* Active cards */}
        {(pitcherCards.length > 0 || batterCards.length > 0) && (
          <div className="flex flex-wrap gap-1.5 justify-center">
            {pitcherCards.map(c => (
              <span key={c.id} className="text-[9px] px-2 py-1 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 font-bold">{c.name}</span>
            ))}
            {batterCards.map(c => (
              <span key={c.id} className="text-[9px] px-2 py-1 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 font-bold">{c.name}</span>
            ))}
          </div>
        )}

        {isOnline && revealCountdown !== null ? (
          <div className="flex flex-col items-center gap-1">
            <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Rolling in</span>
            <span className="text-3xl font-black text-amber-400 tabular-nums animate-pulse">{revealCountdown}</span>
          </div>
        ) : (
          <button onClick={handleStartRolling}
            className="px-6 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-black text-sm transition-all">
            Roll D10s →
          </button>
        )}
      </motion.div>
    );
  }

  // ── Standard / PvP rolling phase ─────────────────────────────────────────
  return (
    <motion.div data-testid="pitching-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex items-center justify-center">
      <div className="flex items-center gap-4 md:gap-16">
        {/* Pitcher column */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center justify-center gap-2">
            <div className="w-6 h-6 rounded bg-red-500/20 flex items-center justify-center text-[9px] font-black text-red-400 border border-red-500/30">P</div>
            <span className="text-xs font-medium text-slate-400 truncate max-w-24">{currentPitcher?.name || 'Pitcher'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-bold">D10</span>
            <input type="text" inputMode="numeric" value={pitcherRoll}
              onChange={(e) => { const v = e.target.value; if (v === "" || (/^\d$/.test(v) && parseInt(v) >= 0 && parseInt(v) <= 9)) setPitcherRoll(v); }}
              placeholder="—"
              className="w-12 h-10 md:w-14 md:h-12 text-xl md:text-2xl text-center font-mono font-black bg-slate-800/80 border-2 border-red-500/50 text-red-400 rounded-lg outline-none focus:ring-2 focus:ring-red-500/40 transition-all"
            />
          </div>
          <div className="flex items-center gap-3">
            {isPvP && chipBonus ? (
              <span className="text-[10px] text-slate-500">
                chip+cards: <span className="font-bold text-red-400">{pitcherChips || "0"}</span>
              </span>
            ) : (
              <>
                <ModInput label="Mgr" value={pitcherManager} onChange={setPitcherManager} />
                <ModInput label="Chip" value={pitcherChips} onChange={setPitcherChips} />
              </>
            )}
          </div>
        </div>

        {/* Center column */}
        <div className="flex flex-col items-center gap-3 min-w-[100px] md:min-w-[140px]">
          {!result ? (
            <div className="flex flex-col items-center gap-2">
              <motion.button data-testid="roll-dice-btn" onClick={rollBothDice} disabled={isRolling || !!isGameOver(game.state, game.innings_total || 9)}
                initial={{ opacity: 1 }} exit={{ opacity: 0 }}
                className={`px-5 py-2 rounded-lg flex items-center gap-2 transition-all active:scale-95 font-bold text-sm shadow-md ${isRolling ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-black shadow-amber-500/20' : 'bg-gradient-to-r from-slate-700 to-slate-800 hover:from-amber-500 hover:to-orange-600 text-white hover:text-black border border-slate-600 hover:border-amber-400'}`}>
                <motion.div animate={isRolling ? { rotate: 360 } : {}} transition={{ duration: 0.3, repeat: isRolling ? Infinity : 0 }}>
                  <Dices size={16} />
                </motion.div>
                Roll D10s
              </motion.button>
              <button onClick={callBall} disabled={isRolling || !!isGameOver(game.state, game.innings_total || 9)}
                className="px-4 py-1.5 rounded-lg text-xs font-bold border border-slate-600 text-slate-400 bg-transparent hover:bg-slate-800 hover:border-slate-500 hover:text-slate-300 transition-all active:scale-95 disabled:opacity-40">
                Call Ball
              </button>
            </div>
          ) : (
            <>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="flex items-center gap-2 text-xs font-mono">
                {hasMods ? (
                  <>
                    <span className="text-red-400">{formatMath(result.pRoll, result.pMgr, result.pChip, result.pitcherTotal)}</span>
                    <span className="text-slate-600">vs</span>
                    <span className="text-amber-400">{formatMath(result.bRoll, result.bMgr, result.bChip, result.batterTotal)}</span>
                  </>
                ) : (
                  <>
                    <span className="text-red-400 font-bold">{result.pitcherTotal}</span>
                    <span className="text-slate-600">vs</span>
                    <span className="text-amber-400 font-bold">{result.batterTotal}</span>
                  </>
                )}
              </motion.div>
              <motion.div initial={{ y: -10, opacity: 0, scale: 0.9 }} animate={{ y: 0, opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: 0.15 }}
                className={`${result.color} rounded-lg py-1.5 px-4 text-white text-center`}>
                <span className="text-sm font-black">{result.label}</span>
              </motion.div>
              <motion.div initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.25 }} className="flex items-center gap-2">
                <button data-testid="reroll-btn" onClick={clear} disabled={processing}
                  className="px-3 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs transition-all disabled:opacity-50">
                  Re-roll
                </button>
                <button data-testid="record-btn" onClick={recordAndAdvance} disabled={processing || !!isGameOver(game.state, game.innings_total || 9)}
                  className="px-4 py-1 rounded-md bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs transition-all disabled:opacity-50">
                  {processing ? "..." : (result.type === "in_play" ? "Fielding" : "Record")}
                </button>
              </motion.div>
            </>
          )}
        </div>

        {/* Batter column */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center justify-center gap-2">
            <div className="w-6 h-6 rounded bg-amber-500/20 flex items-center justify-center text-[9px] font-black text-amber-400 border border-amber-500/30">{currentBatter?.position || 'B'}</div>
            <span className="text-xs font-medium text-slate-400 truncate max-w-24">{currentBatter?.name || 'Batter'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-bold">D10</span>
            <input type="text" inputMode="numeric" value={batterRoll}
              onChange={(e) => { const v = e.target.value; if (v === "" || (/^\d$/.test(v) && parseInt(v) >= 0 && parseInt(v) <= 9)) setBatterRoll(v); }}
              placeholder="—"
              className="w-12 h-10 md:w-14 md:h-12 text-xl md:text-2xl text-center font-mono font-black bg-slate-800/80 border-2 border-amber-500/50 text-amber-400 rounded-lg outline-none focus:ring-2 focus:ring-amber-500/40 transition-all"
            />
          </div>
          <div className="flex items-center gap-3">
            {isPvP && chipBonus ? (
              <span className="text-[10px] text-slate-500">
                chip+cards: <span className="font-bold text-amber-400">{batterChips || "0"}</span>
              </span>
            ) : (
              <>
                <ModInput label="Mgr" value={batterManager} onChange={setBatterManager} />
                <ModInput label="Chip" value={batterChips} onChange={setBatterChips} />
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
